import { useMemo, useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { isFranklinMade } from "@/domains/documents/franklinMade/franklinMade";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useProjectBriefFolder } from "@/domains/brief/hooks/useProjectBriefFolder";
import { formatDateTime } from "@/domains/brief/findingPresentation";
import type { VersionedFolder } from "@/domains/documents/types/folderVersions";
import { fill } from "@/lib/i18n/fill";
import { useAuth } from "@/hooks/useAuth";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { FolderTree } from "@/domains/documents/components/organisms/FolderTree/FolderTree";
import { FolderBreadcrumb } from "@/domains/documents/components/organisms/FolderTree/FolderBreadcrumb";
import { ProjectDocumentView } from "@/domains/projects/components/ProjectDocumentView";
import { ShareDialog } from "@/domains/documents/components/organisms/ShareDialog";
import { useCustomFolders } from "@/domains/documents/hooks/useCustomFolders";
import {
  fetchFolderSubtree,
  getTopLevelFolderIds,
  excludeDocsInFolderSubtrees,
} from "@/domains/documents/utils/folderSelection";
import { injectContainerFolders } from "@/domains/documents/utils/containerFolders";
import { useFolderDragDrop } from "@/domains/documents/hooks/useFolderDragDrop";
import { useExternalFileDrop } from "@/domains/documents/hooks/useExternalFileDrop";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { usePermissions } from "@/hooks/usePermissions";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";
import { PERMISSIONS } from "@/constants/permissions";
import { useTrashOperations } from "@/domains/documents/hooks/useTrashOperations";
import { Button } from "@/components/ui/button";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { PageHeaderActions } from "@/components/layout/PageHeaderActions";
import { CreateGlyph, HeaderActionIconButton } from "@/components/layout/HeaderActionIconButton";
import { PageInfoButton } from "@/components/page-info/PageInfoButton";
import { NewDocumentFlow } from "@/domains/documents/components/templateForm/NewDocumentFlow";
import { FolderViewBackgroundMenu, type FolderViewCreateAction } from "./FolderViewBackgroundMenu";
import {
  ChevronDown,
  FileText,
  Folder,
  FolderInput,
  Upload,
  Trash2,
  CornerLeftUp,
  Link as LinkIcon,
  Loader2,
  MoreVertical,
  Sparkles,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { STORAGE_ADMISSION_EVENT } from "@/domains/documents/utils/storageBatchFeedback";
import { UploadModal } from "@/domains/documents/components/UploadModal";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { folderService, isSystemFolder } from "@/domains/documents/services/folderService";
import {
  notifyIfPinned,
  withoutPinnedDocuments,
} from "@/domains/documents/services/pinnedDocuments";
import { tagIssueAttachmentsFolder } from "@/domains/documents/utils/briefFolder";
import { OrganizeFolderDialog } from "@/domains/documents/components/organize/OrganizeFolderDialog";
import { folderAncestorPath } from "@/domains/documents/types/organize";
import { useFolderOrganize } from "@/domains/documents/hooks/useFolderOrganize";
import { UploadCollisionDialog } from "@/domains/documents/components/organisms/UploadCollisionDialog";
import { UploadQueueProgress } from "@/components/documents/UploadQueueProgress";
import { permanentlyDeleteDocument } from "@/domains/documents/services/documentService";
import { TrashView } from "@/domains/documents/components/TrashView";
import { useToast } from "@/hooks/use-toast";
import type { Document } from "@/types/documents";
import type { TreeFolder } from "@/domains/documents/types";
import type { DocumentHostAction } from "@/domains/documents/types/documentHostAction";
import { FolderPickerDialog } from "./FolderPickerDialog";
import { findTreeFolder, folderSubtreeIds } from "../utils/rootedFolders";
import { CreateFolderDialog } from "./CreateFolderDialog";
import { DocumentSearchField } from "@/domains/documents/components/molecules/DocumentSearchField";
import { ProjectSelectCreateModal } from "@/domains/documents/components/ProjectSelectCreateModal";
import { projectCopyService } from "@/domains/projects/services/projectCopyService";
import type { BuildingProject } from "@/types/projects";
import {
  BuildingStorageQuotaError,
  storageAccessErrorFrom,
  storageQuotaErrorFrom,
} from "@/domains/documents/services/buildingStorageQuota";
import { ProjectDestinationModal, type ProjectDestinationMode } from "./ProjectDestinationModal";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CONTROL_FIELD_CLASS } from "@/components/ui/control-scale";
import { cn } from "@/lib/utils";
import * as documentsRepo from "@/stores/documents/repository";
import {
  fetchChildDocuments,
  fetchProjectContainers,
  searchDocumentContent,
  searchProjectFilenameMatches,
  fetchProjectTagFilteredDocuments,
  fetchDocumentsByIds,
  getAllDescendantFolderIds,
} from "@/stores/documents/repository";
import type { DocumentRowMapped, ContentMatchInfo } from "@/stores/documents/repository";
import { toDocumentWithTags } from "@/domains/documents/utils/documentWithTags";
import {
  resolveDocumentBreadcrumb,
  type FolderPathSegment,
} from "@/domains/documents/utils/folderPathResolver";
import type {
  DocumentWithTags,
  UnifiedSearchRow,
} from "@/domains/documents/components/organisms/DocumentListTable";
import { renameDocument, RenameDocumentValidationError } from "@/domains/documents/repository";
import { useChatStore } from "@/hooks/useChatStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { matchesUploadDateRange } from "@/domains/documents/utils/uploadDateRange";
import { useDocumentFiltersOptional } from "@/domains/documents/context/DocumentFiltersContext";
import { filterDocumentsByTags } from "@/domains/documents/utils/filterDocumentsByTags";
import { parseTagFilters } from "@/domains/documents/utils/parseTagFilters";
import type { UploadSelection } from "@/domains/documents/utils/uploadSelection";
import {
  sortDocuments,
  type SortKey,
  type SortOrder,
} from "@/domains/documents/utils/documentSorting";
import {
  isWritableFolderDropTarget,
  wouldCreateCircularFolderMove,
  type MoveDragSelection,
} from "@/domains/documents/utils/documentMoveDrag";
import type { StagedVersionItem } from "@/domains/documents/types/addVersions";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/**
 * Trim an absolute breadcrumb to a relative one anchored at the user's
 * current folder. Used for WHA-3185 subfolder hits.
 *
 * Rules:
 *   - At project root (currentFolderId == null): drop the leading project
 *     segment, keep the rest.
 *   - Inside a folder: cut everything up to and including the segment whose
 *     folderId matches currentFolderId. If the current folder isn't on the
 *     path (defensive — shouldn't happen with subtree-scoped search), return
 *     null.
 *
 * If the trimmed result has no folder segment (e.g. the file lives in the
 * current folder itself), return null — the row already shows the filename;
 * a single-segment tooltip would be redundant.
 */
function relativeBreadcrumb(
  segments: FolderPathSegment[] | null,
  currentFolderId: string | null
): FolderPathSegment[] | null {
  if (!segments) return null;

  let startIdx = 0;
  if (currentFolderId) {
    const cutIdx = segments.findIndex((s) => s.type === "folder" && s.folderId === currentFolderId);
    if (cutIdx === -1) return null;
    startIdx = cutIdx + 1;
  } else if (segments[0]?.type === "project") {
    startIdx = 1;
  }

  const trimmed = segments.slice(startIdx);
  return trimmed.some((s) => s.type === "folder") ? trimmed : null;
}

/**
 * Sort comparator for the unified search-result row list. When the user has a
 * column-header sort active during a search (WHA-3246), the WHA-3183 tier
 * grouping (filename → folder → content) is replaced by a flat list ordered
 * by the chosen column. Folders interleave with documents using analogous
 * fields (folder name for "name", folder.created_at for date sorts, etc.);
 * null/empty values sort to the end regardless of asc/desc so a sea of
 * date-less rows doesn't bury rows that *do* have the field populated.
 */
function compareUnifiedSearchRow(
  a: UnifiedSearchRow,
  b: UnifiedSearchRow,
  sortKey: SortKey,
  sortOrder: SortOrder
): number {
  const ascending = sortOrder !== "desc";

  const docName = (r: UnifiedSearchRow): string =>
    r.kind === "document" ? r.doc.filename : String((r.folder as any).name ?? "");
  const docModified = (r: UnifiedSearchRow): number | null => {
    const raw =
      r.kind === "document"
        ? r.doc.document_content_modified_date || r.doc.original_created_at || r.doc.created_at
        : ((r.folder as any).updated_at as string | undefined) || (r.folder as any).created_at;
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? null : t;
  };
  const docCreated = (r: UnifiedSearchRow): number | null => {
    const raw =
      r.kind === "document"
        ? // When the file reached Franklin — what the server sorts this key on.
          r.doc.created_at
        : ((r.folder as any).created_at as string | undefined);
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? null : t;
  };
  const docUser = (r: UnifiedSearchRow): string =>
    r.kind === "document"
      ? String(r.doc.created_by_name ?? "")
      : String((r.folder as any).created_by_name ?? "");
  const docSize = (r: UnifiedSearchRow): number | null =>
    r.kind === "document" ? Number(r.doc.size ?? 0) : null;
  const docType = (r: UnifiedSearchRow): string =>
    r.kind === "document" ? String(r.doc.content_type ?? "") : "";

  let av: string | number | null;
  let bv: string | number | null;

  switch (sortKey) {
    case "name":
      av = docName(a);
      bv = docName(b);
      break;
    case "modified_at":
      av = docModified(a);
      bv = docModified(b);
      break;
    case "created_at":
      av = docCreated(a);
      bv = docCreated(b);
      break;
    case "user":
      av = docUser(a);
      bv = docUser(b);
      break;
    case "size":
      av = docSize(a);
      bv = docSize(b);
      break;
    case "type":
      av = docType(a);
      bv = docType(b);
      break;
    default:
      av = docName(a);
      bv = docName(b);
  }

  const aNullish = av === null || av === undefined || av === "";
  const bNullish = bv === null || bv === undefined || bv === "";
  if (aNullish && bNullish) return 0;
  if (aNullish) return 1; // null/empty always sorts last
  if (bNullish) return -1;

  const cmp =
    typeof av === "number" && typeof bv === "number"
      ? av - bv
      : String(av).localeCompare(String(bv));
  return ascending ? cmp : -cmp;
}

interface ProjectScopedFolderViewProps {
  buildingId: string;
  projectId: string;
  projectName?: string;
  /**
   * The building's name, supplied by hosts that already hold it (/documents,
   * which loads the building detail for its own header). It enables the "New
   * document" action — the template picker titles its recommended section with
   * it and the form pre-fills the object name from it — so the action is
   * offered only where the name is known, never with a blank stand-in.
   */
  buildingName?: string;
  /** Opens the Manage Shares dialog (owned by the page, which renders it). */
  onManageShares?: () => void;
  /**
   * Set when the host page renders the search box in its own page-level tools
   * row (/documents). The inline box is then dropped and the query is read
   * straight from the shared DocumentFiltersContext. Standalone hosts
   * (/projects/:id) leave this false and keep the inline box.
   */
  hideInlineSearch?: boolean;
  /**
   * Set when the host page already shows the project in a page-level breadcrumb
   * of its own (the building context line on /documents). The folder view then
   * drops the standalone project-name heading — which would otherwise repeat
   * the project at root level — and moves root navigation into the tree's root
   * item. The folder path itself still renders above the document table once a
   * folder is selected: it's the only way back up level by level. Standalone
   * hosts (/projects/:id) leave this false and keep the project heading as the
   * root link.
   */
  hideProjectName?: boolean;
  /** Notifies the host when this view enters/leaves its per-project trash. */
  onTrashViewChange?: (isOpen: boolean) => void;
  /**
   * Roots the view at one folder of the project instead of the project root —
   * the Brief page's view of the project's Brief folder. The folder stands in
   * for "no folder" everywhere: the breadcrumb starts at it, navigation never
   * leaves its subtree, and uploads, new folders and drops "at the root" land
   * in it. There is no folder tree (the host keeps that column for its own
   * panel): subfolders are rows, reached back through the breadcrumb and Up.
   * Organise and the trash are not offered; the host owns those surfaces.
   */
  rootFolder?: { id: number; name: string };
  /**
   * Renders the "Search in folder" box into a slot the host owns (the Brief
   * page's tab row) instead of this view's header row. `null` while the slot
   * is still mounting — the box then waits for it rather than falling back
   * inline. Unlike `hideInlineSearch`, the view keeps owning the query.
   */
  searchSlot?: HTMLElement | null;
  /**
   * Renders the folder path (and Up) into a slot the host owns — the Brief
   * page's Documents header — instead of a row of its own over the table.
   * `null` while the slot is still mounting.
   */
  trailSlot?: HTMLElement | null;
  /** The host page's own action on documents (the Brief page's "Add appendix"). */
  documentAction?: DocumentHostAction;
  /**
   * Leaves the Tags column out, for a host that gives the list less room —
   * the Brief page, while Franklin's suggestions take a column beside it.
   */
  hideTags?: boolean;
  /**
   * Turns Upload into an "Add files" menu with a second way in: picking
   * existing documents (the Brief page's "Add existing", which copies them in).
   */
  addExisting?: { menuLabel: string; label: string; onSelect: () => void };
}

/**
 * How many documents whose folder does not exist the project root will list.
 *
 * One `.in()` chunk of `fetchDocumentsByIds`, so the recovery costs a single
 * round trip however broken the data is. These rows are a repair affordance —
 * somewhere to see a misfiled document and drag it into a real folder — not a
 * view of their own, and a folder soft-deleted with thousands of documents
 * under it would otherwise flood the root list it is meant to rescue them into.
 */
const UNREACHABLE_ROOT_LIMIT = 200;

/**
 * Stable empty result for the unreachable-document query, which is disabled in
 * the common case (no broken rows) and so resolves `data` as `undefined`. A
 * `= []` default in the destructuring would allocate a fresh array on every
 * render and, being a dependency of the `documents` memo, invalidate it and
 * every derivation downstream of it.
 */
const NO_UNREACHABLE_DOCS: DocumentRowMapped[] = [];

/**
 * Sentry needs a real Error, and a refused move can arrive as anything — an
 * Error, a PostgREST object, or nothing at all when RLS simply filtered the
 * rows out of the UPDATE.
 */
function moveFailureError(firstError: unknown): Error {
  if (firstError instanceof Error) return firstError;
  if (firstError === undefined) {
    return new Error("Move declined without an error (RLS filtered the write)");
  }
  return new Error(String((firstError as { message?: string })?.message ?? firstError));
}

/** One readable line per refused write, for the errors the report carries as extra. */
function describeMoveError(error: unknown): string {
  return error instanceof Error ? `${error.name}: ${error.message}` : String(error);
}

export const ProjectScopedFolderView = observer(function ProjectScopedFolderView({
  buildingId,
  projectId,
  projectName,
  buildingName,
  onManageShares,
  hideInlineSearch = false,
  hideProjectName = false,
  onTrashViewChange,
  rootFolder,
  searchSlot,
  trailSlot,
  documentAction,
  hideTags = false,
  addExisting,
}: ProjectScopedFolderViewProps) {
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const {
    hasPermission,
    canModifyData,
    canModifyFilesInProject,
    canWriteFilesInProject,
    canWriteFoldersInProject,
    canSoftDeleteInProject,
  } = usePermissions();
  const { toast: toastUi } = useToast();

  // Document store for project document loading via direct folder_id queries
  const documentStore = useDocumentStore();
  const chatStore = useChatStore();
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeMove, setActiveMove] = useState<MoveDragSelection | null>(null);
  const folderIdFromUrl = searchParams.get("folderId");
  // What "no folder" means here: the project root (null), or the folder the
  // view is rooted at. Every write and every navigation that would otherwise
  // reach the project root goes through `toViewFolder`.
  const viewRootId = rootFolder ? String(rootFolder.id) : null;
  const toViewFolder = useCallback(
    (folderId: string | null) => folderId ?? viewRootId,
    [viewRootId]
  );
  // A non-numeric id is not a folder id at all: it can be judged without a
  // round trip, and querying a bigint column with it would only error.
  const isFolderIdWellFormed = !!folderIdFromUrl && /^\d+$/.test(folderIdFromUrl);
  // Which folder the loader below may open. A malformed id names no folder, so
  // it opens the project root instead of spending a guaranteed-failing query on
  // it — the scope check further down strips the param right after.
  const navigableFolderId = isFolderIdWellFormed ? folderIdFromUrl : viewRootId;

  // Scope the store to what this view renders, wherever it is mounted. The
  // documents page sets the same scope for its other tabs, but /projects/:id
  // has no other owner of it and never calls loadDocuments — so without this
  // the store kept the last building the user visited, and the building fence
  // in `resolveSelectionForAction` refused every row a bulk action recovered
  // there (Sign opening on 0 documents, bulk download silently shrinking).
  useEffect(() => {
    if (buildingId) documentStore.setDocumentScope(buildingId, projectId);
  }, [buildingId, projectId, documentStore]);

  // Document filters context — provided by BuildingProjects so the column-header
  // sort UI in DocumentListTable is wired the same way as on /documents.
  // The project document store doesn't accept sort params (results are cached
  // per folder), so we apply the sort client-side over the in-memory array.
  const docFiltersContext = useDocumentFiltersOptional();
  const sortKey = docFiltersContext?.filters.sortKey;
  const sortOrder = docFiltersContext?.filters.sortOrder;
  // Tag filters come from the same shared context, set by the host page's
  // Filters control. The project document cache is per-folder and unfiltered,
  // so they're applied client-side over the rows below. The project scope is
  // the single source of truth for the project dimension here (every row
  // already belongs to the scoped project, and the Filters control locks its
  // Projects group), so `project-*` values lingering from an unscoped
  // Timeline/List session are stripped — mirroring the scope-wins override in
  // BuildingDocuments' documentFilters. Without this a stale project filter
  // silently blanks the whole view.
  const rawTagFilters = docFiltersContext?.filters.activeTagFilters;
  // The Added filter: applied by the subtree fetch below and, for rows this
  // view holds already (the folder cache, search hits), client-side.
  const addedRange = docFiltersContext?.filters.addedDate?.range;
  const activeTagFilters = useMemo(
    () => rawTagFilters?.filter((value) => !value.startsWith("project-")),
    [rawTagFilters]
  );
  const availableFilters = documentStore.availableFilters;

  // Category/filter-group/filter-item/filter-merged values resolve to ids via
  // availableFilters. While it's still null those dimensions can't be applied,
  // so the table would silently show unfiltered rows under an active filter
  // chip. Treat that window as pending (spinner below) until the options load;
  // a failed load drops the pending state and toasts instead.
  const hasUnresolvedNameFilters = useMemo(() => {
    if (availableFilters || !activeTagFilters?.length) return false;
    return activeTagFilters.some(
      (value) =>
        value.startsWith("category-") ||
        value.startsWith("filter-group-") ||
        value.startsWith("filter-item-") ||
        value.startsWith("filter-merged-")
    );
  }, [activeTagFilters, availableFilters]);
  const isAwaitingFilterOptions = hasUnresolvedNameFilters && !documentStore.filtersLoadFailed;

  const filtersLoadFailed = documentStore.filtersLoadFailed;
  // One toast per failure episode: without the ref gate, removing and
  // re-adding a chip while the same failed load persists re-fires the toast
  // on every hasUnresolvedNameFilters false→true flip. Resets when the
  // failure clears so a later, distinct failure toasts again.
  const filtersFailureToastedRef = useRef(false);
  useEffect(() => {
    if (!filtersLoadFailed) {
      filtersFailureToastedRef.current = false;
      return;
    }
    if (hasUnresolvedNameFilters && !filtersFailureToastedRef.current) {
      filtersFailureToastedRef.current = true;
      toast.error(t.documents.tagFilter.valuesLoadFailed);
    }
  }, [hasUnresolvedNameFilters, filtersLoadFailed, t.documents.tagFilter.valuesLoadFailed]);

  // Numeric tag-dimension ids + the Verified toggle for the scope-wide filter
  // fetch below (the project dimension is inert here — scope-stripped above).
  // null = nothing selected that the subtree fetch can act on, so it doesn't
  // run. Verified-only alone still fetches, so it covers subfolders the same
  // as List/Timeline.
  const parsedTagFilterIds = useMemo(() => {
    if (!activeTagFilters?.length && !addedRange) return null;
    const { categoryIds, designDisciplineIds, designStageIds, verifiedOnly } = parseTagFilters(
      activeTagFilters ?? [],
      availableFilters
    );
    const hasAnyDimension =
      [categoryIds, designDisciplineIds, designStageIds].some((ids) => ids?.length) ||
      !!verifiedOnly ||
      !!addedRange;
    return hasAnyDimension
      ? {
          categoryIds,
          designDisciplineIds,
          designStageIds,
          verifiedOnly,
          uploadDateRange: addedRange,
        }
      : null;
  }, [activeTagFilters, availableFilters, addedRange]);

  // Local folder selection state (replaces mapping.selectedFolderId)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(viewRootId);

  // Search state for the inline box (only rendered when the host page doesn't
  // own the search — see `hideInlineSearch`).
  const [localSearchText, setLocalSearchText] = useState("");
  // Per-document match metadata returned by the content-search RPC. Used by
  // the Content chip to render "Found on page 3 (+4 more)" (WHA-3169).
  const [contentMatchInfo, setContentMatchInfo] = useState<Map<number, ContentMatchInfo>>(
    new Map()
  );
  // Full document data for filename matches across the current folder's
  // subtree (WHA-3185 — subfolder hits surface as direct file rows).
  const [filenameMatchDocs, setFilenameMatchDocs] = useState<DocumentRowMapped[]>([]);
  // Full document data for content-only matches (not in filename results)
  const [contentOnlyDocs, setContentOnlyDocs] = useState<DocumentRowMapped[]>([]);
  // True while the parallel filename + content search RPCs are in flight.
  // Drives the document-list spinner overlay (WHA-3247) when search is the
  // reason for the pending state, complementing documentStore.isLoadingProjectDocs.
  const [isSearchInFlight, setIsSearchInFlight] = useState(false);

  // Whichever box owns the search, the value read here is already a COMMITTED
  // query — DocumentSearchField only hands one over on Enter, the search
  // button, clear, or its idle timer. Nothing to debounce a second time.
  const searchText = hideInlineSearch
    ? (docFiltersContext?.filters.searchQuery ?? "")
    : localSearchText;

  // What this view actually narrows by — the scope-stripped tags, not the raw
  // context — so an empty folder only claims "no match" when a filter is the
  // reason. Without a provider (/projects) the search is this view's own box.
  const hasActiveViewFilters =
    (activeTagFilters?.length ?? 0) > 0 || !!addedRange || searchText.trim().length > 0;
  // The search may be this view's own box rather than the context's, so the
  // empty state's Clear has to empty both or it leaves the list unchanged.
  const clearContextFilters = docFiltersContext?.clearFilters;
  const clearViewFilters = useCallback(() => {
    clearContextFilters?.();
    setLocalSearchText("");
  }, [clearContextFilters]);

  // Run search when the committed search text changes. Scope to the current
  // project (WHA-3179 — no cross-project leak) and to the current folder +
  // its subtree (WHA-3168 — placeholder reads "Search in folder…"). At the
  // project root the folder subtree is the whole project. Two parallel calls:
  // a subtree filename search and the content-search RPC. Both populate
  // local state used to build the unified result rows below (WHA-3185).
  //
  // Also reports the resolved query up to the host page's search box when it
  // owns the box (hideInlineSearch) — that box renders outside this view and
  // has no fetch of its own to watch.
  const reportResolved = docFiltersContext?.reportResolvedSearchQuery;
  useEffect(() => {
    const report = (query: string | null) => {
      if (hideInlineSearch) reportResolved?.(query);
    };

    const trimmed = searchText.trim();
    if (!trimmed) {
      setContentMatchInfo(new Map());
      setFilenameMatchDocs([]);
      setContentOnlyDocs([]);
      setIsSearchInFlight(false);
      // The cleared search has nothing to fetch, so it is resolved already —
      // the rows the view falls back to are the ones it never dropped.
      report(searchText);
      return;
    }

    const parsedProjectId = parseInt(projectId, 10);
    if (Number.isNaN(parsedProjectId)) return;
    const parsedFolderId = selectedFolderId ? parseInt(selectedFolderId, 10) : NaN;

    let cancelled = false;
    setIsSearchInFlight(true);
    report(null);
    (async () => {
      try {
        let folderIds: number[] | null = null;
        if (!Number.isNaN(parsedFolderId)) {
          try {
            folderIds = await getAllDescendantFolderIds([parsedFolderId]);
          } catch (e) {
            console.warn("Failed to compute folder subtree for search:", e);
            folderIds = [parsedFolderId];
          }
          if (cancelled) return;
        }

        const [filenameResult, matchInfo] = await Promise.all([
          searchProjectFilenameMatches({
            buildingId,
            projectId: parsedProjectId,
            folderIds,
            searchText: trimmed,
          }),
          searchDocumentContent(buildingId, trimmed, {
            projectId: parsedProjectId,
            folderIds,
          }),
        ]);
        if (cancelled) return;
        setFilenameMatchDocs(filenameResult.documents);
        setContentMatchInfo(matchInfo);

        const ids = Array.from(matchInfo.keys());
        const contentDocs = ids.length > 0 ? await fetchDocumentsByIds(ids) : [];
        if (cancelled) return;
        setContentOnlyDocs(contentDocs);
      } finally {
        if (!cancelled) {
          setIsSearchInFlight(false);
          // Reported on failure too: a box left spinning forever reads worse
          // than one that stops on rows the view kept.
          report(searchText);
        }
      }
    })();

    // No setIsSearchInFlight(false) here. This cleanup runs on EVERY dep
    // change, including the one that starts the next search, so clearing the
    // flag here made the overlay blink off between consecutive searches. The
    // `cancelled` flag already stops the superseded run from writing anything.
    return () => {
      cancelled = true;
    };
  }, [searchText, buildingId, projectId, selectedFolderId, hideInlineSearch, reportResolved]);

  // Handing the box back when this tab unmounts, so the view that takes over
  // is the one that says when its rows have landed.
  useEffect(() => {
    if (!hideInlineSearch) return;
    return () => reportResolved?.(null);
  }, [hideInlineSearch, reportResolved]);

  // Inline rename for documents in the project view. Uses the validated
  // repository function and refetches on success. Permission gating: org-wide
  // MODIFY_DATA OR project-scoped MODIFY_PROJECT_FILES on this project.
  const handleRenameDocumentSubmit = useCallback(
    async (docId: string, newFilename: string) => {
      const numericId = parseInt(docId, 10);
      if (Number.isNaN(numericId)) return;
      try {
        await renameDocument(numericId, newFilename);
        // Patch in-memory caches so the row label updates instantly without
        // waiting for the refetch to round-trip. (WHA-3259)
        documentStore.applyRename(String(numericId), newFilename);
        // Refetch is defined later in the file via useCallback; call the store
        // directly here to avoid a forward reference.
        if (projectId) {
          documentStore.invalidateProjectCache(projectId);
          documentStore.loadFolderDocumentCounts(projectId, buildingId);
          if (selectedFolderId) {
            await documentStore.loadFolderDocuments(
              projectId,
              selectedFolderId,
              sortKey,
              sortOrder
            );
          } else {
            await documentStore.loadProjectRootDocuments(projectId, sortKey, sortOrder);
          }
        }
        toastUi({
          title: t.documents.rename.success,
          description: t.documents.rename.successDescription.replace("{name}", newFilename),
        });
      } catch (error) {
        const description = (() => {
          if (error instanceof RenameDocumentValidationError) {
            const c = error.cause;
            switch (c.kind) {
              case "empty":
                return t.documents.rename.errorEmpty;
              case "tooLong":
                return t.documents.rename.errorTooLong.replace("{max}", String(c.max));
              case "invalidChars":
                return t.documents.rename.errorInvalidChars;
              case "extensionChanged":
                return t.documents.rename.errorExtensionChanged.replace(
                  "{ext}",
                  c.expected || "(none)"
                );
              case "collision":
                return t.documents.rename.errorCollision.replace("{name}", c.existingFilename);
              case "notFound":
                return t.documents.rename.errorNotFound;
              case "unknown":
                return c.message || t.documents.rename.errorUnknown;
            }
          }
          console.error("Rename error:", error);
          return t.documents.rename.errorUnknown;
        })();
        toastUi({
          variant: "destructive",
          title: t.documents.rename.errorTitle,
          description,
        });
      }
    },
    [buildingId, documentStore, projectId, selectedFolderId, sortKey, sortOrder, t, toastUi]
  );

  const numericProjectId = useMemo(() => {
    const n = parseInt(projectId, 10);
    return Number.isNaN(n) ? null : n;
  }, [projectId]);

  // The project's Brief folder is shown as the brief: its name follows the UI
  // language, it carries the Brief icon, and its row lists the brief's
  // confirmed versions, each opening on the Brief page.
  const briefFolder = useProjectBriefFolder(numericProjectId);
  const navigate = useNavigate();

  const canRenameDocumentInProject = useCallback(() => {
    if (canModifyData) return true;
    if (numericProjectId === null) return false;
    return canModifyFilesInProject(numericProjectId);
  }, [canModifyData, canModifyFilesInProject, numericProjectId]);
  const canReorganizeProject = canRenameDocumentInProject();

  // What this user may add to, and bin from, this project. Owners and admins
  // pass; a member answers from their per-project permissions, which an admin
  // can narrow per project — so these are real questions, not formalities.
  // Affordances only: RLS refuses the write whatever the UI shows. A member
  // without the permission can still press Upload / New folder / New document
  // or drop files; the press is refused with a message naming who can grant
  // it, rather than a greyed-out control that explains nothing.
  const lacksAddPermission = numericProjectId !== null && !canWriteFilesInProject(numericProjectId);
  const lacksAddFolderPermission =
    numericProjectId !== null && !canWriteFoldersInProject(numericProjectId);
  const denyPermission = usePermissionDenied();
  const refuseAdd = () => void denyPermission("addFiles", numericProjectId);
  const canMoveToTrash = numericProjectId !== null && canSoftDeleteInProject(numericProjectId);

  // Refetch documents via store - invalidate cache and reload
  const refetchDocuments = useCallback(async () => {
    if (projectId) {
      documentStore.invalidateProjectCache(projectId);
      // Refresh folder counts from server (documents may have moved)
      documentStore.loadFolderDocumentCounts(projectId, buildingId);
      if (selectedFolderId) {
        await documentStore.loadFolderDocuments(projectId, selectedFolderId, sortKey, sortOrder);
      } else {
        await documentStore.loadProjectRootDocuments(projectId, sortKey, sortOrder);
      }
    }
  }, [projectId, buildingId, selectedFolderId, sortKey, sortOrder, documentStore]);

  // The URL is the source of truth for folder navigation. Folder clicks push a
  // `?folderId=` location below, so browser Back/Forward changes this value and
  // runs the same loader as tree and breadcrumb navigation.
  // Tracks (projectId + folderIdFromUrl) so that:
  // - Re-runs from currentOrganization/userProfile ref changes are skipped (same key)
  // - Browser history navigation to a different folder triggers a new load
  const lastInitKeyRef = useRef<string | null>(null);
  const previousNavigationProjectIdRef = useRef<string | null>(null);
  const folderNavigationRequestRef = useRef(0);
  const scrollAfterFolderNavigationRef = useRef(false);
  // Bumped when the user re-selects the folder the URL already points at.
  // That click pushes no history entry, so it needs its own trigger — see
  // handleFolderSelect.
  const [folderReloadTick, setFolderReloadTick] = useState(0);
  // Whether the load for the location the URL currently names gave up. Only
  // the wording of the write gate depends on it: a target that failed to open
  // is blocked the same as one still opening, but telling the user to "try
  // again in a moment" about a load that has already given up sends them
  // waiting for nothing.
  const [folderDocumentsLoadFailed, setFolderDocumentsLoadFailed] = useState(false);
  useEffect(() => {
    if (!projectId || !currentOrganization || !userProfile) return;

    const initKey = `${projectId}:${navigableFolderId || ""}:${folderReloadTick}`;
    if (lastInitKeyRef.current === initKey) return;
    lastInitKeyRef.current = initKey;

    const previousProjectId = previousNavigationProjectIdRef.current;
    previousNavigationProjectIdRef.current = projectId;
    const isFirstLoad = previousProjectId === null;
    const projectChanged = !isFirstLoad && previousProjectId !== projectId;
    if (projectChanged) {
      documentStore.invalidateProjectCache(projectId);
      documentStore.clearFolderCounts();
      // A folder belongs to exactly one project, so the selection can't be
      // deferred across a project switch the way a same-project move is:
      // holding the old id for the length of the round-trip would point the
      // breadcrumb, the create-folder dialog and — worst — an upload at a
      // folder outside the project now on screen.
      setSelectedFolderId(navigableFolderId);
    }

    // Consume the scroll intent up front rather than in the callback below: a
    // superseded request returns early, and leaving the flag set would make a
    // later Back/Forward step scroll when it should restore its own position.
    const shouldScroll = scrollAfterFolderNavigationRef.current;
    scrollAfterFolderNavigationRef.current = false;

    const requestId = ++folderNavigationRequestRef.current;
    setFolderDocumentsLoadFailed(false);
    const loadPromise = navigableFolderId
      ? documentStore.loadFolderDocuments(projectId, navigableFolderId, sortKey, sortOrder)
      : documentStore.loadProjectRootDocuments(projectId, sortKey, sortOrder);

    // A failed load is the one outcome the user must not be left in silently:
    // the selection is committed on success below, so a failure that says
    // nothing would leave the URL on folder B while the breadcrumb, the table
    // and every write target stay on folder A. The selection deliberately
    // stays where it is — the rows on screen are folder A's — and the pending
    // gate further down blocks writes for as long as the two disagree.
    // Retry re-runs this effect: the tick is part of its key. Clearing the key
    // too keeps a plain re-run (an org/profile ref change) from
    // short-circuiting on a load that never landed, and the consumed scroll
    // intent goes back so the retry still scrolls where this run meant to.
    const reportFolderLoadFailed = () => {
      if (folderNavigationRequestRef.current !== requestId) return;
      setFolderDocumentsLoadFailed(true);
      lastInitKeyRef.current = null;
      scrollAfterFolderNavigationRef.current = shouldScroll;
      toast.error(t.projects.folder.documentsLoadFailed, {
        action: {
          label: t.common.retry,
          onClick: () => setFolderReloadTick((tick) => tick + 1),
        },
      });
    };

    void loadPromise
      .then((loadedDocuments) => {
        // A quick Back/Forward sequence can resolve requests out of order. Only
        // the newest URL location may become the visible folder.
        if (folderNavigationRequestRef.current !== requestId) return;
        // The loaders catch their own failures, so a failed read arrives here
        // and not in the `catch` below — as `null`, the one result they never
        // produce for a folder that simply holds no documents.
        if (loadedDocuments === null) {
          reportFolderLoadFailed();
          return;
        }
        setSelectedFolderId(navigableFolderId);

        if (shouldScroll) {
          requestAnimationFrame(() => {
            mainContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      })
      // Nothing in the loaders rejects today, but this promise is the only
      // thing holding the selection back: were one to reject — a torn-down
      // store, a throw outside their try — the same recovery has to run, and
      // an unhandled rejection instead would strand the view with no toast.
      .catch(reportFolderLoadFailed);

    // Counts are per project, not per folder — refetching them on every folder
    // step would issue a full count query per click and per Back/Forward. A run
    // that finds none held picks them up anyway: the store keeps null after a
    // failed load, and the project-changed run above cleared the previous
    // project's, so without this the tree would show no counts at all until an
    // unrelated write (an upload, a rename, a refetch) happened to reload them.
    const countsNeedReload =
      documentStore.folderDocumentCounts === null && !documentStore.isLoadingFolderCounts;
    if (isFirstLoad || projectChanged || countsNeedReload) {
      void documentStore.loadFolderDocumentCounts(projectId, buildingId).catch(() => {
        // Logged by the store, which also leaves the counts null — so the next
        // run of this effect (a folder step, a retry) asks for them again.
      });
    }
    // sortKey/sortOrder intentionally omitted from deps: the init effect's
    // lastInitKeyRef is keyed by (projectId, navigableFolderId) and a sort change
    // is handled by the dedicated sort-change effect below. Including them
    // here would race the two effects on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    buildingId,
    currentOrganization,
    userProfile,
    documentStore,
    navigableFolderId,
    folderReloadTick,
  ]);

  // Sort-change effect: when the user toggles a column header in the document
  // list, kick off a server-side requery for the current folder. We do NOT
  // invalidate the project cache here — the loaders compare cached
  // sort-meta to the requested sort and refetch on mismatch, atomically
  // replacing the slot when the new rows arrive. That keeps the previously
  // sorted rows visible under the loading overlay during the round-trip
  // (WHA-3246-followup) so the document list never collapses to height 0
  // and the page-level scrollbar doesn't jerk up/down. Skipped on the very
  // first run — the init effect above already loaded the initial sort.
  const lastSortSigRef = useRef<string | null>(null);
  useEffect(() => {
    const sortSig = `${sortKey ?? ""}|${sortOrder ?? ""}`;
    if (lastSortSigRef.current === null) {
      lastSortSigRef.current = sortSig;
      return;
    }
    if (lastSortSigRef.current === sortSig) return;
    lastSortSigRef.current = sortSig;

    if (!projectId || !currentOrganization || !userProfile) return;
    if (selectedFolderId) {
      documentStore.loadFolderDocuments(projectId, selectedFolderId, sortKey, sortOrder);
    } else {
      documentStore.loadProjectRootDocuments(projectId, sortKey, sortOrder);
    }
  }, [
    sortKey,
    sortOrder,
    projectId,
    selectedFolderId,
    currentOrganization,
    userProfile,
    documentStore,
  ]);

  // Subscribe to realtime document updates for this building
  // This enables automatic UI updates when documents transition from processing to complete
  // "folders": tells the store that a resync must re-read the open folder slot
  // rather than the paginated list — the two are tabs over one store, and it
  // has no other way to know which is mounted.
  useEffect(() => {
    if (buildingId && currentOrganization && userProfile) {
      documentStore.subscribe(buildingId, null, "folders");
      return () => {
        documentStore.unsubscribe();
      };
    }
  }, [buildingId, currentOrganization, userProfile, documentStore]);

  // Documents of this project whose folder_id names no live folder OF this
  // project — one in another project, or a soft-deleted one. There is no node
  // to render them under and they are not at the root either, so before this
  // they appeared nowhere in the Folders tab while List and Timeline kept
  // showing them. List them at the project root: reachable, selectable, and
  // draggable into a real folder, which is what actually repairs the row.
  // Ids come from the folder-count load, which already reads both sides.
  // Bounded on purpose: one soft-deleted folder can hold thousands of
  // documents, and every one of them would otherwise become a root row, a
  // chunked round trip and a kilometre of query key. The sort is NUMERIC —
  // ids compared as strings put "1000" before "99", so the cap would keep a
  // lexicographic slice rather than the oldest N — and it makes the query key
  // canonical: the id list comes back in whatever order the count query
  // scanned, and a reshuffle is not a new question.
  const unreachableDocIds = useMemo(
    () =>
      documentStore.unreachableDocIds
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id))
        .sort((a, b) => a - b)
        .slice(0, UNREACHABLE_ROOT_LIMIT),
    [documentStore.unreachableDocIds]
  );
  const unreachableIdsKey = unreachableDocIds.join(",");
  const { data: unreachableDocs = NO_UNREACHABLE_DOCS } = useQuery({
    queryKey: ["project-unreachable-docs", projectId, unreachableIdsKey],
    queryFn: () => documentsRepo.fetchDocumentsByIds(unreachableDocIds),
    // Listed at the project root only, which a rooted view never shows.
    enabled: !viewRootId && unreachableDocIds.length > 0,
  });

  // Say so when the cap bites. These rows are the only place a misfiled
  // document is visible at all, so dropping the rest silently leaves them
  // unreachable with nothing on screen admitting it. Keyed on the hidden
  // count, so a re-render at the same count does not re-toast.
  const unreachableHiddenCount = documentStore.unreachableDocIds.length - unreachableDocIds.length;
  useEffect(() => {
    if (viewRootId || unreachableHiddenCount <= 0) return;
    toast.warning(
      t.documents.folders.unreachableTruncated
        .replace("{count}", String(UNREACHABLE_ROOT_LIMIT))
        .replace("{total}", String(UNREACHABLE_ROOT_LIMIT + unreachableHiddenCount))
    );
  }, [viewRootId, unreachableHiddenCount, t.documents.folders.unreachableTruncated]);

  // Convert store documents to the format expected by downstream components,
  // reading the cache with this component's own selectedFolderId — immune to
  // external mutations of the store's global currentFolderId.
  //
  // Keyed on `projectCacheVersion`, NOT on the result of getProjectDocuments:
  // that is a view METHOD rather than a computed, so it returns a fresh array
  // on every call and memoising on it would hand every downstream consumer a
  // new identity on every render. The counter is the store's own freshness
  // signal (every cache write bumps it) and is read here on each render, so
  // MobX keeps tracking it. `unreachableDocs` is React Query's own array, whose
  // identity is stable between refetches, so it can be a dependency directly.
  const projectCacheVersion = documentStore.projectCacheVersion;
  const documents = useMemo(() => {
    void projectCacheVersion; // the cache is volatile; this counter is what changes
    const cached = documentStore.getProjectDocuments(projectId, selectedFolderId);
    // Only at the project root: inside a folder the rows are that folder's, and
    // an unreachable document belongs to none of them.
    const rows = selectedFolderId === null ? [...cached, ...unreachableDocs] : cached;
    return rows.map((doc: any) => ({
      id: String(doc.id),
      filename: doc.filename,
      file_path: doc.file_path,
      building_id: String(doc.building_id),
      parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
      content_type: doc.content_type,
      size: doc.size,
      created_at: doc.created_at,
      original_created_at: doc.original_created_at,
      modified_at: doc.modified_at,
      document_content_modified_date: doc.document_content_modified_date,
      created_by: doc.created_by,
      created_by_name: doc.created_by_name,
      created_by_avatar_url: doc.created_by_avatar_url,
      created_by_color: doc.created_by_color,
      verified_at: doc.verified_at ?? null,
      verified_by_name: doc.verified_by_name ?? null,
      state: doc.state,
      version: doc.version,
      version_group: doc.version_group,
      source: doc.source ?? null,
      project_id: doc.project_id,
      folder_id: doc.folder_id ? String(doc.folder_id) : null,
      is_deleted: doc.is_deleted,
      categories: doc.categories ?? [],
      design_disciplines: doc.design_disciplines ?? [],
      design_stages: doc.design_stages ?? [],
      linked_issues: doc.linked_issues ?? [],
    }));
  }, [documentStore, projectId, selectedFolderId, projectCacheVersion, unreachableDocs]);

  // Scope-wide rows for active tag filters in browse mode (no search). The
  // per-folder cache only covers the open folder, so filtering it client-side
  // blanked the table even when a subfolder had matches — unlike List/
  // Timeline, which filter server-side across the whole scope. When a tag
  // dimension or the Verified toggle is active this fetches every match in the
  // current folder's subtree (whole project at root); the rows render through
  // the unified search-row path so each carries a relative breadcrumb. null =
  // fetch not active (no tag filters, or a text search owns the view).
  const [tagFilterDocs, setTagFilterDocs] = useState<DocumentRowMapped[] | null>(null);
  const [isTagFilterFetchInFlight, setIsTagFilterFetchInFlight] = useState(false);
  // True when the last fetch hit the row cap and dropped older matches. The
  // toast effect below fires only on the false→true transition, so refetches
  // that stay truncated (e.g. store reloads) don't re-toast.
  const [isTagFilterTruncated, setIsTagFilterTruncated] = useState(false);

  useEffect(() => {
    if (isTagFilterTruncated) {
      toast.warning(t.documents.tagFilter.truncated);
    }
  }, [isTagFilterTruncated, t.documents.tagFilter.truncated]);

  useEffect(() => {
    if (searchText.trim() || !parsedTagFilterIds) {
      setTagFilterDocs(null);
      setIsTagFilterFetchInFlight(false);
      setIsTagFilterTruncated(false);
      return;
    }

    const parsedProjectId = parseInt(projectId, 10);
    if (Number.isNaN(parsedProjectId)) return;
    const parsedFolderId = selectedFolderId ? parseInt(selectedFolderId, 10) : NaN;

    let cancelled = false;
    setIsTagFilterFetchInFlight(true);
    (async () => {
      try {
        let folderIds: number[] | null = null;
        if (!Number.isNaN(parsedFolderId)) {
          // A failed subtree computation falls through to the outer catch —
          // silently narrowing to the current folder would look like "no
          // matches in subfolders" with no error surfaced.
          folderIds = await getAllDescendantFolderIds([parsedFolderId]);
          if (cancelled) return;
        }
        const { documents: docs, truncated } = await fetchProjectTagFilteredDocuments({
          buildingId,
          projectId: parsedProjectId,
          folderIds,
          ...parsedTagFilterIds,
        });
        if (!cancelled) {
          setTagFilterDocs(docs);
          setIsTagFilterTruncated(truncated);
        }
      } catch (e) {
        if (cancelled) return;
        console.warn("Tag-filtered subtree fetch failed:", e);
        // Drop back to the folder-local client filter rather than keeping
        // stale scope-wide rows, and tell the user subfolder matches are out.
        setTagFilterDocs(null);
        setIsTagFilterTruncated(false);
        toast.error(t.documents.tagFilter.loadFailed);
      } finally {
        if (!cancelled) setIsTagFilterFetchInFlight(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `projectCacheVersion` is the freshness signal: every mutation path
    // (upload, move, delete, rename, realtime) bumps it, so the filtered subtree
    // rows re-sync with the cache. It must NOT be the mapped `documents` array
    // — `getProjectDocuments` is a view METHOD, not a computed, so it hands back
    // a fresh array on every render. Depending on that identity made this effect
    // re-run on every render, and its own `setTagFilterDocs` caused one: an
    // endless refetch that pinned `isTagFilterFetchInFlight` (and so the
    // table's spinner) true for as long as any tag filter was active.
  }, [
    searchText,
    parsedTagFilterIds,
    buildingId,
    projectId,
    selectedFolderId,
    projectCacheVersion,
    t.documents.tagFilter.loadFailed,
  ]);

  // Compute content-only doc IDs (those not in filename results) for the store indicator
  const contentOnlyDocIds = useMemo(() => {
    const trimmed = searchText.trim().toLowerCase();
    if (!trimmed || contentOnlyDocs.length === 0) return new Set<string>();

    const filenameMatchIds = new Set(
      documents
        .filter((doc: any) => doc.filename.toLowerCase().includes(trimmed))
        .map((d: any) => String(d.id))
    );
    return new Set(
      contentOnlyDocs.map((d) => String(d.id)).filter((id) => !filenameMatchIds.has(id))
    );
  }, [documents, searchText, contentOnlyDocs]);

  // Sync content match IDs + per-doc page info to the document store so
  // DocumentTableRow can render the Content chip and its tooltip
  // ("Found on page 3 (+4 more)") via store reads (WHA-3169).
  useEffect(() => {
    documentStore.setContentMatchDocIds(contentOnlyDocIds);
    const infoMap = new Map<string, ContentMatchInfo>();
    for (const id of contentOnlyDocIds) {
      const info = contentMatchInfo.get(Number(id));
      if (info) infoMap.set(id, info);
    }
    documentStore.setContentMatchInfoById(infoMap);
    return () => {
      documentStore.setContentMatchDocIds(new Set());
      documentStore.setContentMatchInfoById(new Map());
    };
  }, [contentOnlyDocIds, contentMatchInfo, documentStore]);

  // Trash operations for soft delete from project view
  const trashOperations = useTrashOperations({
    buildingId,
    onRefresh: async () => {
      await refetchDocuments();
    },
  });

  // Project-scoped folder tree (reuse existing custom folders, scoped by projectId)
  const {
    folders: projectFolders,
    isLoading: foldersLoading,
    foldersError,
    folderTree,
    createFolder,
    updateFolder,
    moveFolderAsync,
    deleteFolder,
    findFolderById,
    refetchFolders,
  } = useCustomFolders({
    buildingId,
    projectId,
    userId: String(userProfile?.id || ""),
    orgId: String(currentOrganization?.id || ""),
  } as any);

  // `?folderId=` is navigation state anyone can edit, and it outlives the
  // folder it names — a bookmark survives that folder being deleted or moved
  // to another project. Nothing downstream re-checks it: `selectedFolderId`
  // is what uploads, new subfolders and filed documents are written into, so
  // an id outside this project would file them where neither view shows them.
  // An id that does not belong to this project is dropped, and the URL effect
  // above falls back to the project root.
  //
  // What the id has to prove is project membership — NOT that this view's tree
  // drew it. `useCustomFolders` lists only the folders it draws (`is_custom`
  // AND `is_project_scoped`); a project also holds folders carrying neither
  // flag — archive containers, system folders ("Issue Attachments"), rows
  // created by a backend or an older build — and the building view lists all
  // of them and links straight into them with `?projectId=&folderId=`. Judging
  // the id by the drawn list alone would bounce every such link to the project
  // root and disable every write inside those folders.
  //
  // So a hit in the list is proof (the query is keyed by building + project,
  // and `document_folders.id` is a bigint the tree stringifies), and any other
  // id is resolved against its own row. That row read also settles what the
  // list cannot: its 5-minute staleTime serves a cached list without
  // refetching, so a folder someone else created inside that window is simply
  // missing from it. The strip is irreversible (`replace: true` drops the
  // param from history), so it acts only on an answer the server just gave —
  // while the read is in flight, or if it failed, the id is neither stripped
  // nor vouched for. The key carries (building, project, id): this component
  // outlives a project switch (BuildingDocuments keeps it mounted), so a
  // verdict reached in project 7 must not decide the same id in project 8.
  const isListedFolder =
    !!folderIdFromUrl && projectFolders.some((folder) => String(folder.id) === folderIdFromUrl);
  // The row's project decides membership, and it alone. A project sits in
  // exactly one building, so a matching `project_id` already implies this
  // building — whereas `document_folders.building_id` is nullable, and the rows
  // this read exists to rescue (containers, system folders, rows from an older
  // build) are the likeliest to carry no building at all. Comparing that column
  // too would judge such a row foreign and strip it out of its own project.
  //
  // `project_id` is nullable as well, and `Number(null)` is 0 — so the
  // comparison happens between two real ids or not at all: a row naming no
  // project is foreign, and a blank `projectId` prop (`Number("") === 0`) never
  // gets to vouch for one, because the query doesn't run without a parsed id.
  const scopeProjectId = /^\d+$/.test(projectId) ? Number(projectId) : null;
  // `isFolderIdWellFormed` is computed up top — the document loader needs it too.
  const {
    data: urlFolderScope,
    error: urlFolderScopeError,
    refetch: refetchUrlFolderScope,
  } = useQuery({
    queryKey: ["url-folder-scope", buildingId, projectId, folderIdFromUrl],
    queryFn: async () => {
      const folder = await folderService.getFolderById(folderIdFromUrl as string);
      // Parsed, not stringified: the row's id is a number and the prop is
      // whatever the URL carried, so `8` and `"08"` are the same project.
      const rowProjectId = folder?.project_id ?? null;
      return rowProjectId !== null && Number(rowProjectId) === scopeProjectId
        ? ("in-project" as const)
        : ("foreign" as const);
    },
    // `isListedFolder` is false while the list is still loading, so without the
    // `foldersLoading` gate every deep link into an ordinary listed folder would
    // spend this query before the list it is already in arrives — and a failure
    // on that wasted read would toast a load error for a folder nothing is
    // actually blocked on.
    // Rooted views open listed folders only (the subtree check above).
    enabled:
      !viewRootId &&
      !!folderIdFromUrl &&
      !isListedFolder &&
      isFolderIdWellFormed &&
      !foldersLoading &&
      scopeProjectId !== null,
    // Never answer from a cached verdict: the sighting being judged is this
    // one, and the row can have moved project since the last.
    staleTime: 0,
    gcTime: 0,
  });
  // Rooted, the id must also sit inside the root's subtree — the page shows
  // that folder and nothing else of the project, so a link to any other folder
  // is as foreign as one from another project. Judged once the list holds the
  // root itself; until then (or if the list failed) the id is not stripped.
  const rootSubtreeIds = useMemo(
    () => (viewRootId ? folderSubtreeIds(projectFolders, viewRootId) : null),
    [viewRootId, projectFolders]
  );
  const isUrlFolderOutsideRoot =
    !!folderIdFromUrl && !!rootSubtreeIds && !rootSubtreeIds.has(folderIdFromUrl);
  const isUrlFolderInProject =
    !isUrlFolderOutsideRoot && (isListedFolder || urlFolderScope === "in-project");
  const isUrlFolderForeign =
    isUrlFolderOutsideRoot ||
    (!!folderIdFromUrl &&
      !isListedFolder &&
      (!isFolderIdWellFormed || urlFolderScope === "foreign"));

  useEffect(() => {
    if (!isUrlFolderForeign) return;
    const next = new URLSearchParams(searchParams);
    next.delete("folderId");
    setSearchParams(next, { replace: true });
  }, [isUrlFolderForeign, searchParams, setSearchParams]);

  // react-query stops after its own retries, and nothing else on this page
  // refetches either of these. A fetch that fails with no list ever received
  // leaves the tree empty and the write gates below disabled behind a tooltip
  // promising a retry that never comes, and only a full reload recovers.
  // Surface the failure and hand the retry over. Keyed on the error objects, so
  // a retry that fails again says so.
  //
  // The row read counts as much as the list: it is the only thing that can
  // vouch for an open folder the tree never lists (container/system/legacy —
  // exactly what that read exists for), so its failure disables the same
  // writes, and with `staleTime`/`gcTime` at 0 nothing refetches it either.
  // One toast for both — whichever failed is what Retry re-runs.
  useEffect(() => {
    if (!foldersError && !urlFolderScopeError) return;
    toast.error(t.projects.folder.folderLoadFailed, {
      action: {
        label: t.common.retry,
        onClick: () => {
          if (foldersError) void refetchFolders();
          if (urlFolderScopeError) void refetchUrlFolderScope();
        },
      },
    });
  }, [
    foldersError,
    urlFolderScopeError,
    refetchFolders,
    refetchUrlFolderScope,
    t.projects.folder.folderLoadFailed,
    t.common.retry,
  ]);

  // That check above is the only thing validating `?folderId=`, and it needs a
  // folder list or a row read. `selectedFolderId` meanwhile is committed as
  // soon as the *documents* load resolves, one round trip earlier, so there is
  // a window where the open folder is an unvalidated, user-editable URL value.
  // Reading it during that window costs an empty list at worst; WRITING to it
  // files documents outside the project on screen (`handleBulkUpload` doesn't
  // cross-check that the parent folder belongs to the project it uploads
  // into). So every write target is gated on something vouching for the id
  // first — not silently redirected to the project root, which is just as
  // wrong a destination. Upload, New folder and New document all write into it,
  // so all three carry the gate, and New document re-checks it at file time —
  // its form is long-lived, and a refetch can drop the folder mid-form.
  // Why writing into `folderId` is blocked, or undefined when it is allowed. A
  // failed lookup is not "still opening" — that wording tells the user to wait
  // for something that has already given up. Takes the folder as an argument
  // because the target isn't always the open folder: New folder writes into
  // whichever parent its dialog was opened for.
  //
  // The gate is the list being in hand, not the last fetch having succeeded: a
  // failed fetch leaves react-query's `status` at "error" while KEEPING the
  // last good `data`, so a routine refetch that fails — window focus past the
  // 5-minute staleTime — would otherwise disable every write against a folder
  // list that is complete, correct, and still rendering the row the user is
  // standing in. `projectFolders` is keyed by (building, project), so a hit
  // here is this project's folder whatever the last fetch did; an empty list
  // can't produce one. The open folder can also be one the list never carries
  // (a container/system/legacy folder — see the scope check above), and then
  // its own row is what vouches for it. The strip keeps the stricter "the
  // server just confirmed it" reading — it is irreversible, this isn't.
  const folderBlockedReason = useCallback(
    (folderId: string | null): string | undefined => {
      if (!folderId) return undefined;
      // The root a rooted view was given is the host's to vouch for (the Brief
      // page reads it from the brief's own row), and it may be newer than the
      // cached folder list.
      if (folderId === viewRootId) return undefined;
      if (projectFolders.some((folder) => String(folder.id) === folderId)) {
        return undefined;
      }
      if (folderId === folderIdFromUrl && isUrlFolderInProject) {
        return undefined;
      }
      return foldersError || urlFolderScopeError
        ? t.projects.folder.folderLoadFailed
        : t.projects.folder.folderTargetUnconfirmed;
    },
    [
      projectFolders,
      folderIdFromUrl,
      isUrlFolderInProject,
      foldersError,
      urlFolderScopeError,
      t.projects.folder.folderLoadFailed,
      t.projects.folder.folderTargetUnconfirmed,
      viewRootId,
    ]
  );

  // `selectedFolderId` catches up with the URL only once the open folder's
  // documents land, so between a `?folderId=` navigation and that load
  // resolving the committed target is still the PREVIOUS location — `null` on
  // a deep link, and on the first drill-in from the project root. The gate
  // below reads `null` as "the project root", which is always a legal target,
  // so without this every write control stays enabled through that window and
  // aimed one level out: an upload, a new folder or a filed document started
  // there lands in the root while the URL — and, a moment later, the
  // breadcrumb — say folder 5. A folder this view has not opened yet is
  // precisely what the gate exists to refuse, whether the id turns out to be
  // foreign or merely late, so the catch-up window is blocked too. It ends
  // when the load commits the selection, or reports that it failed — which is
  // not "still opening" and mustn't be worded as such.
  const isFolderTargetPending = selectedFolderId !== navigableFolderId;
  const pendingFolderTargetReason = folderDocumentsLoadFailed
    ? t.projects.folder.documentsLoadFailed
    : t.projects.folder.folderTargetUnconfirmed;
  const folderTargetBlockedReason = isFolderTargetPending
    ? pendingFolderTargetReason
    : folderBlockedReason(selectedFolderId);
  const isFolderTargetConfirmed = folderTargetBlockedReason === undefined;

  // Fetch all container documents (ZIP/archive) for the project to inject into folder tree
  const { data: projectContainers = [], refetch: refetchContainers } = useQuery({
    queryKey: ["project-containers", projectId],
    queryFn: () => fetchProjectContainers(projectId),
    enabled: !!projectId,
  });

  // Refs for container click handling — avoids initialization order issues with useCallback
  const containerClickRef = useRef<(docId: string, filename: string) => void>();
  const projectContainersRef = useRef(projectContainers);
  const mainContentRef = useRef<HTMLElement>(null);
  projectContainersRef.current = projectContainers;

  // Container navigation stack (for navigating into .zip/.asice files). Declared
  // above handleFolderSelect because folder navigation leaves every open
  // container.
  interface ContainerStackEntry {
    id: string;
    filename: string;
    children: any[];
  }
  const [containerStack, setContainerStack] = useState<ContainerStackEntry[]>([]);

  // Drilled into a container, the list shows that container's members
  // (`parent_document_id` set), not a folder's contents — a move would file one
  // of them under a folder while the container still owns it, from a view where
  // every other folder operation is already switched off. So the list offers no
  // move at all while a container is open, which also stops those rows being
  // drag sources.
  const isViewingContainer = containerStack.length > 0;
  const canMoveFromList = canReorganizeProject && !isViewingContainer;

  // Folder navigation is URL navigation: each selection pushes a history
  // entry, and the URL-driven effect above loads it before swapping the table.
  const handleFolderSelect = useCallback(
    (folderId: string | null) => {
      // Handle container (virtual folder) clicks — navigate into the container
      if (folderId?.startsWith("container-")) {
        const docId = folderId.replace("container-", "");
        const container = projectContainersRef.current.find((c) => c.id === docId);
        containerClickRef.current?.(docId, container?.filename ?? "Archive");
        return;
      }

      // Rooted, the root has no `?folderId=` of its own: selecting it is going
      // back to the view's root, so both spellings share one URL.
      const target = folderId === viewRootId ? null : folderId;

      // Every folder navigation — tree, breadcrumb, back-to-root button — leaves
      // the container the user drilled into. The selectedFolderId effect below
      // only covers moves to a DIFFERENT folder; re-selecting the current one
      // (the trail's own last segment) has to clear the stack too.
      setContainerStack([]);

      const next = new URLSearchParams(searchParams);
      if (target) {
        next.set("folderId", target);
      } else {
        next.delete("folderId");
      }
      scrollAfterFolderNavigationRef.current = true;
      if (next.toString() === searchParams.toString()) {
        // Re-selecting the folder the URL already points at: there is no new
        // history entry to push, but the click is also the de-facto retry of a
        // load that failed (the store loaders swallow the error and resolve to
        // an empty list), so re-run the loader for this location.
        setFolderReloadTick((tick) => tick + 1);
        return;
      }
      setSearchParams(next, { preventScrollReset: true });
    },
    [searchParams, setSearchParams, viewRootId]
  );

  /** True when the folder already sits directly in the target — nothing to write. */
  const isFolderAlreadyIn = (folderId: string, targetFolderId: string | null) => {
    const source = findFolderById?.(folderId);
    return !!source && (source.parentId ?? null) === targetFolderId;
  };

  // The one move-folder write behind every drop target — root zone, tree,
  // breadcrumb and list. Dropping a folder on the parent it already lives in
  // writes nothing, so it is refused here rather than reported as "Folder moved
  // successfully" — the same no-op `handleMoveDocument` refuses (WHA-3261).
  // Refused, but not in silence: every folder row advertises itself as a
  // destination while a move is in flight, so a drop that does nothing at all
  // reads as a broken drag rather than as "it is already here".
  const handleMoveFolder = async (folderId: string, requestedTargetId: string | null) => {
    const targetFolderId = toViewFolder(requestedTargetId);
    if (isFolderAlreadyIn(folderId, targetFolderId)) {
      toast.info(t.projects.folder.alreadyInFolder);
      return;
    }
    try {
      // Awaited, not `moveFolder` (react-query's fire-and-forget `mutate`): the
      // row leaves the selection only once the move has actually committed, so
      // a rejected move — RLS, network — leaves the user with the selection
      // they were operating on, ready to retry. The mutation reports the
      // failure itself.
      await moveFolderAsync({ folderId, newParentId: targetFolderId });
      setSelectedFolderIds((ids) => ids.filter((id) => id !== folderId));
    } catch (error) {
      console.error("Move folder failed:", error);
      return;
    }
    // The badges are recursive totals, so a folder changing parent restates
    // both chains — every document under it leaves one total and joins the
    // other. The move only refetches the folder rows, and the counts are a
    // separate query, so without this the old parent keeps counting documents
    // that are no longer under it until an unrelated document write happens.
    //
    // Outside the try, and with its own catch: this is a second query against
    // an already-committed move, so its failure is stale badges, not a failed
    // move. Inside, it would be logged as one — after the mutation had already
    // toasted the move as done — and the folder loader's own counts call keeps
    // it separate for the same reason.
    await documentStore.loadFolderDocumentCounts(projectId, buildingId).catch(() => {
      // Logged by the store, which also leaves the counts null — so the next
      // run of the folder effect asks for them again.
    });
  };

  // Drag-and-drop handler for root drop zone
  const rootDragDrop = useFolderDragDrop(folderTree || [], {
    onDropDocument: canReorganizeProject
      ? (docId, targetFolderId) => handleMoveDocument(docId, targetFolderId)
      : undefined,
    onMoveFolder: canReorganizeProject ? handleMoveFolder : undefined,
    // Arrows, not direct references: both handlers are consts declared further
    // down, so they're still in their temporal dead zones while this object
    // literal is evaluated.
    onDropSelection: canReorganizeProject
      ? (docIds, folderIds, targetFolderId) =>
          handleMoveSelection(docIds, folderIds, targetFolderId)
      : undefined,
    onDropFiles: (selection, targetFolderId) => handleFileDropOnFolder(selection, targetFolderId),
  });

  // Loading state from store
  const docsLoading = documentStore.isLoadingProjectDocs;

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ buildingId: string; orgId: string }>).detail;
      if (detail.buildingId === buildingId && detail.orgId === currentOrganization?.id)
        setIsUploadModalOpen(true);
    };
    window.addEventListener(STORAGE_ADMISSION_EVENT, listener);
    return () => window.removeEventListener(STORAGE_ADMISSION_EVENT, listener);
  }, [buildingId, currentOrganization?.id]);
  const [showTrash, setShowTrash] = useState(false);

  // Mirrors the aside's @[1080px] container breakpoint in JS so the folder
  // trail can track whether the tree (and the root item at its top) is
  // actually visible: below it the trail brings its own back-to-root button
  // and drops its leading chevron. Measured in a layout effect so the first
  // paint already has the right answer instead of flashing the narrow layout.
  // Keep the 1080 threshold in sync with the aside's `@[1080px]:block` below.
  const folderViewContainerRef = useRef<HTMLDivElement>(null);
  const [isFolderTreeVisible, setIsFolderTreeVisible] = useState(false);
  useLayoutEffect(() => {
    const el = folderViewContainerRef.current;
    if (!el) return;
    // Fractional widths only: clientWidth rounds to an integer, so at e.g.
    // 1079.6px it would report 1080 while the @[1080px] container query keeps
    // the aside hidden — hiding both the tree and its back-to-root fallback.
    // Content box, not border box: @container size queries measure the content
    // box, so subtract border+padding to stay in sync with the observer path
    // (contentBoxSize) if the container ever gains padding.
    const measure = () => {
      const style = getComputedStyle(el);
      const contentWidth =
        el.getBoundingClientRect().width -
        ((parseFloat(style.borderLeftWidth) || 0) +
          (parseFloat(style.borderRightWidth) || 0) +
          (parseFloat(style.paddingLeft) || 0) +
          (parseFloat(style.paddingRight) || 0));
      setIsFolderTreeVisible(contentWidth >= 1080);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      // Window resize is a coarse proxy for container width, but without it
      // the mount-time answer freezes and narrowing the window would hide the
      // aside while the back-to-root fallback stays suppressed.
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const width = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      setIsFolderTreeVisible(width >= 1080);
    });
    observer.observe(el);
    return () => observer.disconnect();
    // showTrash swaps the whole container subtree in/out, remounting the
    // observed element — re-run to observe the fresh node.
  }, [showTrash]);
  // Use document store selection instead of local state for document selection
  const selectedDocIds = documentStore.selectedDocumentIds;
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  // Number of folder-selection cascades currently resolving. A folder check
  // marks the folder selected instantly, then asynchronously selects every
  // descendant doc; while any cascade is in flight the bulk action bar shows
  // a spinner for the count and disables its actions.
  const [pendingCascadeCount, setPendingCascadeCount] = useState(0);

  const handleContainerClick = useCallback(async (docId: string, filename: string) => {
    try {
      const data = await fetchChildDocuments(docId);
      const mapped = data.map((doc) => ({
        id: String(doc.id),
        filename: doc.filename,
        file_path: doc.file_path,
        building_id: String(doc.building_id),
        parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
        content_type: doc.content_type || "",
        size: doc.size || 0,
        created_at: doc.created_at,
        original_created_at: doc.original_created_at,
        created_by: doc.created_by,
        created_by_name: doc.created_by_name,
        created_by_avatar_url: doc.created_by_avatar_url,
        created_by_color: doc.created_by_color,
        verified_at: doc.verified_at ?? null,
        verified_by_name: doc.verified_by_name ?? null,
        state: doc.state,
        version: doc.version,
        version_group: doc.version_group,
        project_id: doc.project_id,
        folder_id: doc.folder_id ? String(doc.folder_id) : null,
        categories: doc.categories ?? [],
        design_disciplines: doc.design_disciplines ?? [],
        design_stages: doc.design_stages ?? [],
        linked_issues: doc.linked_issues ?? [],
      }));
      setContainerStack((prev) => {
        // Guard against duplicate entries (e.g., double-click from sidebar)
        if (prev.length > 0 && prev[prev.length - 1].id === docId) return prev;
        return [...prev, { id: docId, filename, children: mapped }];
      });
    } catch (err) {
      console.error("Failed to load container children:", err);
      toast.error(t.projects.folder.failedOpenContainer);
    }
  }, []);
  containerClickRef.current = handleContainerClick;

  const handleExitContainer = useCallback((depth?: number) => {
    if (depth !== undefined) {
      // Navigate to a specific depth in the stack (for breadcrumb clicks)
      setContainerStack((prev) => prev.slice(0, depth));
    } else {
      // Pop the last entry
      setContainerStack((prev) => prev.slice(0, -1));
    }
  }, []);

  // Reset container stack when folder or project changes
  useEffect(() => {
    setContainerStack([]);
  }, [selectedFolderId, projectId]);

  const [isFolderPickerOpen, setIsFolderPickerOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<"move" | "copy" | "newProject" | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isProjectDestinationOpen, setIsProjectDestinationOpen] = useState(false);
  const [isProjectOperationLoading, setIsProjectOperationLoading] = useState(false);

  // Share Folder dialog state (separate from bulk share)
  const [isShareFolderDialogOpen, setIsShareFolderDialogOpen] = useState(false);
  const [shareFolderId, setShareFolderId] = useState<string | null>(null);

  // Create Folder dialog state (replaces native prompt)
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [createFolderParentId, setCreateFolderParentId] = useState<string | null>(null);

  // New document (template picker → form). Same flow the Timeline and List
  // tabs open. The building name arrives as a prop and is deliberately NOT
  // fetched here: the host page gates its own render on that same fetch, so a
  // second loader inside this view would flip the gate back to "loading",
  // unmount this view, and re-fire on the next mount — an endless spinner.
  const [isNewDocumentModalOpen, setIsNewDocumentModalOpen] = useState(false);
  const newDocumentButtonRef = useRef<HTMLButtonElement>(null);

  // Single entry point for opening the Create Folder dialog. Used by the
  // header button, the FolderTree's add/subfolder actions, and the document
  // list's folder context menu so all three share one code path.
  const openCreateFolderDialog = useCallback(
    (parentId: string | null) => {
      if (!currentOrganization?.id) {
        toast.error(t.projects.folder.selectOrganization);
        return;
      }
      setCreateFolderParentId(parentId);
      setCreateFolderDialogOpen(true);
    },
    [currentOrganization?.id, t.projects.folder.selectOrganization]
  );

  // Load projects for the building (for move/copy to project feature)
  const { projects } = useProjects(buildingId);

  // Refetch trash data when switching to trash view
  useEffect(() => {
    if (showTrash) {
      trashOperations.refetch();
    }
  }, [showTrash, trashOperations.refetch]);

  // The trash has its own toolbar, so tell the host to drop its page-level
  // tools row while it's open.
  useEffect(() => {
    onTrashViewChange?.(showTrash);
    // Unmounting (e.g. a tab switch) leaves the trash, so clear the host's flag.
    return () => onTrashViewChange?.(false);
  }, [showTrash, onTrashViewChange]);

  // Clear document selection when leaving the page
  useEffect(() => {
    return () => {
      documentStore.clearSelection();
    };
  }, [documentStore]);

  // Document upload hook with project integration
  const { handleBulkUpload, isUploading, addVersionsInOrder, collisionDialog } = useDocumentUpload({
    buildingId,
    projectId,
    onUploadComplete: async () => {
      await refetchFolders();
      await refetchDocuments();
      await refetchContainers();
      documentStore.loadFolderDocumentCounts(projectId, buildingId);
    },
  });

  /** Adapter from DocumentWithTags-shaped rows → addVersionsInOrder. */
  const handleAddNewVersions = useCallback(
    async (
      items: StagedVersionItem[],
      doc: {
        id: string;
        filename: string;
        version_group: string | null;
        project_id?: number | null;
        folder_id?: string | null;
        parent_document_id: string | null;
        container_type?: string | null;
      }
    ) => {
      return await addVersionsInOrder(
        items,
        {
          id: doc.id,
          filename: doc.filename,
          version_group: doc.version_group,
          project_id: doc.project_id ?? parseInt(projectId, 10),
          folder_id: doc.folder_id ? parseInt(doc.folder_id, 10) : null,
          parent_document_id: doc.parent_document_id,
          container_type: doc.container_type ?? null,
        },
        projectId
      );
    },
    [addVersionsInOrder, projectId]
  );

  const selectedFolder = useMemo(() => {
    if (!selectedFolderId) return null;
    const found = findFolderById?.(selectedFolderId as string) ?? null;
    // The Brief folder is known by id; the breadcrumb and headers show it as the brief.
    if (found && String(found.id) === briefFolder.folderId) {
      return { ...found, variant: "brief" as const, displayName: t.brief.title };
    }
    return found && isSystemFolder(found)
      ? {
          ...found,
          variant: "issue-attachments" as const,
          displayName: t.documents.folders.issueAttachments,
        }
      : found;
  }, [
    selectedFolderId,
    findFolderById,
    briefFolder.folderId,
    t.brief.title,
    t.documents.folders.issueAttachments,
  ]);

  // ── AI folder organization (WHA-3654) ────────────────────────────────
  // Mirrors the backend gate (platform admin / org owner bypass via
  // hasPermission, else project-scoped MODIFY_PROJECT_FILES).
  const canOrganizeFolder = useMemo(() => {
    if (canModifyData) return true;
    const n = parseInt(projectId, 10);
    return !Number.isNaN(n) && canModifyFilesInProject(n);
  }, [canModifyData, canModifyFilesInProject, projectId]);

  // id null = the project root (loose files with no folder).
  const [organizeTarget, setOrganizeTarget] = useState<{
    id: number | null;
    name: string;
  } | null>(null);
  const [isOrganizeOpen, setIsOrganizeOpen] = useState(false);

  // Named in the dialog header so a run is unambiguous in a building with
  // several projects. Derived here because this view already holds the tree.
  const organizeFolderPath = useMemo(
    () => folderAncestorPath(folderTree ?? [], organizeTarget?.id ?? null),
    [folderTree, organizeTarget]
  );

  const handleOrganizeFolder = useCallback(
    (folderId: string) => {
      const folder = findFolderById?.(folderId);
      // The toolbar's target is confirmed against the URL, which can vouch for
      // a folder the TREE has not listed yet — so the lookup can still miss on
      // a deep link. Say so: the button is enabled, and a click that opened
      // nothing at all reads as the feature being broken.
      if (!folder) {
        toast.error(t.projects.folder.folderTargetUnconfirmed);
        return;
      }
      if (isSystemFolder(folder)) return;
      setOrganizeTarget({ id: parseInt(folderId, 10), name: folder.name });
      setIsOrganizeOpen(true);
    },
    [findFolderById, t.projects.folder.folderTargetUnconfirmed]
  );

  const refreshAfterOrganize = useCallback(() => {
    refetchFolders?.();
    refetchDocuments();
  }, [refetchFolders, refetchDocuments]);

  // Watches the CURRENT folder's organize run so the toolbar can show an
  // "Organizing…" pill (and reopen the dialog) after the dialog was closed.
  const { request: selectedFolderOrganizeRequest } = useFolderOrganize({
    buildingId,
    projectId,
    folderId: selectedFolderId ? parseInt(selectedFolderId, 10) : null,
  });

  // The organise control has three states and they drive both its icon and its
  // colour, so name them once rather than repeating the status list per prop.
  const organizeRunning =
    !!selectedFolderOrganizeRequest &&
    ["pending", "running", "applying"].includes(selectedFolderOrganizeRequest.status);
  const organizeAwaitingReview = selectedFolderOrganizeRequest?.status === "proposal_ready";

  // Documents flowing into ProjectDocumentView's `documents` prop.
  //
  // Browse mode (no search): the current folder's docs.
  //
  // Search mode: the union of subtree filename matches + content matches,
  // deduped by id. The visual order is driven by `orderedSearchRows` inside
  // DocumentListTable; this array exists so the table's ancillary machinery
  // — `documentTagsMap`, `documentFolderPaths`, the propsDoc lookup in the
  // preview/delete/download handlers, and the bulk-action gathering loops —
  // can find every search hit. Without including subtree filename hits here
  // the rows rendered without tags and with non-functional action buttons
  // because those code paths iterate the `documents` prop, not
  // `orderedSearchRows`.
  // On the Brief page (a view rooted at the Brief folder) Franklin's
  // additions PDF is hidden: it is Franklin's rendering of the additions
  // shown right below, not one of the brief's files. /documents keeps it, so
  // the Brief folder (and its download) carries the additions. Decided from
  // the row's own `source`, so a PDF filed a moment ago is hidden on arrival.
  const hideFranklinMade = rootFolder !== undefined;

  const filteredDocuments = useMemo(() => {
    // Name-based chips can't resolve until availableFilters loads; falling
    // through to filterDocumentsByTags(…, null) would show every row
    // unfiltered under an active chip. Hold the list empty — isRefreshing
    // (which includes isAwaitingFilterOptions) keeps the spinner up.
    if (isAwaitingFilterOptions) return [];
    const trimmed = searchText.trim();
    if (!trimmed) {
      // Once the scope-wide tag-filter fetch lands its rows replace the
      // folder-local list, so matches in subfolders surface — same scope
      // semantics as List/Timeline. Until it resolves (or when no tag
      // dimension is active) fall back to the folder-local client filter.
      if (tagFilterDocs !== null) {
        return tagFilterDocs.map(toDocumentWithTags);
      }
      return filterDocumentsByTags(documents, activeTagFilters ?? [], availableFilters).filter(
        (doc) => matchesUploadDateRange(doc.created_at, addedRange)
      );
    }

    const seen = new Set<string>();
    const out: DocumentWithTags[] = [];

    for (const doc of filenameMatchDocs) {
      const mapped = toDocumentWithTags(doc);
      if (!seen.has(mapped.id)) {
        seen.add(mapped.id);
        out.push(mapped);
      }
    }
    for (const doc of contentOnlyDocs) {
      const mapped = toDocumentWithTags(doc);
      if (!seen.has(mapped.id)) {
        seen.add(mapped.id);
        out.push(mapped);
      }
    }
    return filterDocumentsByTags(out, activeTagFilters ?? [], availableFilters).filter((doc) =>
      matchesUploadDateRange(doc.created_at, addedRange)
    );
  }, [
    documents,
    searchText,
    filenameMatchDocs,
    contentOnlyDocs,
    activeTagFilters,
    availableFilters,
    addedRange,
    tagFilterDocs,
    isAwaitingFilterOptions,
  ]);

  const visibleDocuments = useMemo(
    () =>
      hideFranklinMade
        ? filteredDocuments.filter((doc) => !isFranklinMade(doc))
        : filteredDocuments,
    [filteredDocuments, hideFranklinMade]
  );

  // Browse-mode sort is applied server-side via fetchProjectDocuments
  // (WHA-3246). The store cache invalidates on column-header toggle and
  // refetches with the new order, so `filteredDocuments` already comes back
  // in the right order — no second client-side pass needed.

  // Container children (virtual folders inside a .zip / .asice) are loaded
  // ad-hoc and don't go through fetchProjectDocuments, so their column-header
  // sort still happens client-side.
  const sortedContainerChildren = useMemo(() => {
    if (containerStack.length === 0) return [];
    const children = containerStack[containerStack.length - 1].children;
    if (!sortKey || !sortOrder) return children;
    return sortDocuments(children as any, sortKey, sortOrder);
  }, [containerStack, sortKey, sortOrder]);

  // Enrich folder tree with document counts from server-side query
  // Uses documentStore.folderDocumentCounts which contains pre-computed recursive totals
  const enrichedFolderTree = useMemo(() => {
    if (!folderTree) return folderTree;

    const folderCounts = documentStore.folderDocumentCounts;

    const enrichFolder = (folder: any): any => {
      const enrichedChildren = folder.children.map(enrichFolder);

      // Get count from server-side computed totals, fallback to 0
      const folderCountInfo = folderCounts?.get(folder.id);
      const totalDocumentCount = folderCountInfo?.totalCount ?? 0;

      return {
        ...folder,
        documentCount: totalDocumentCount,
        children: enrichedChildren,
        ...(String(folder.id) === briefFolder.folderId
          ? { variant: "brief" as const, displayName: t.brief.title }
          : {}),
      };
    };

    return tagIssueAttachmentsFolder(
      folderTree.map(enrichFolder),
      t.documents.folders.issueAttachments
    );
  }, [
    folderTree,
    documentStore.folderDocumentCounts,
    briefFolder.folderId,
    t.brief.title,
    t.documents.folders.issueAttachments,
  ]);

  const versionedFolder = useMemo<VersionedFolder | undefined>(() => {
    if (!briefFolder.folderId || briefFolder.versions.length === 0) return undefined;
    const copy = t.brief.versions;
    return {
      folderId: briefFolder.folderId,
      versions: briefFolder.versions.map((v) => ({
        id: v.id,
        versionNo: v.version_no,
        createdAt: v.created_at,
        confirmedByName: v.confirmed_by_name,
        note: v.note,
      })),
      toggleLabel: t.tools.drawingCompare.showVersions,
      title: (v) => fill(copy.label, { number: String(v.versionNo) }),
      meta: (v) =>
        fill(copy.confirmedBy, {
          name: v.confirmedByName ?? "—",
          date: formatDateTime(v.createdAt, language),
        }),
      onOpen: (v) =>
        navigate(`/buildings/${buildingId}/brief?project=${projectId}&version=${v.versionNo}`),
    };
  }, [briefFolder, t.brief.versions, language, navigate, buildingId, projectId]);

  // Inject navigable container documents (plain .zip) as virtual folder
  // entries. Signed containers are excluded by `injectContainerFolders` — they
  // are files, not folders, and belong in the list as signed-container rows.
  const folderTreeWithContainers = useMemo(() => {
    if (!enrichedFolderTree || projectContainers.length === 0) return enrichedFolderTree;
    return injectContainerFolders(enrichedFolderTree, projectContainers, buildingId);
  }, [enrichedFolderTree, projectContainers, buildingId]);

  // Rooted: the root is the top of the breadcrumb, and its children are what
  // the folder picker offers.
  const rootNode = useMemo(
    () =>
      viewRootId && folderTreeWithContainers
        ? findTreeFolder(folderTreeWithContainers, viewRootId)
        : null,
    [viewRootId, folderTreeWithContainers]
  );
  // A rooted view has no folder tree.
  const showTreeAside = !viewRootId;
  const treeShown = showTreeAside && isFolderTreeVisible;
  // Rooted and at the root with no container open, the trail would name only
  // the root itself, so it takes no room.
  const atViewRoot = viewRootId !== null && selectedFolderId === viewRootId;
  const showTrailSlot = !viewRootId || !atViewRoot || containerStack.length > 0;

  // The same box the /documents toolbar uses, so search behaves identically on
  // both pages: commit on Enter, on the search button, or after a pause — never
  // on every keystroke.
  const searchField = (
    <DocumentSearchField
      value={localSearchText}
      onCommit={setLocalSearchText}
      isBusy={isSearchInFlight}
      placeholder={t.documents.table.searchInFolder}
    />
  );
  const trailFolders = viewRootId ? (rootNode ? [rootNode] : []) : enrichedFolderTree || [];

  // Helper to find a folder in the enriched tree by ID (recursive)
  const findEnrichedFolder = useCallback(
    (folderId: string, folders: any[] = enrichedFolderTree || []): any | null => {
      for (const folder of folders) {
        if (folder.id === folderId) return folder;
        if (folder.children?.length) {
          const found = findEnrichedFolder(folderId, folder.children);
          if (found) return found;
        }
      }
      return null;
    },
    [enrichedFolderTree]
  );

  // Handler for Export Folder context menu action — delegates to the shared,
  // structure-preserving zip builder. It resolves the folder subtree, filters
  // the documents exactly like the document list does (no trashed / hidden /
  // DWG-child / container-child rows), keeps empty subfolders, and names the
  // zip after the folder.
  const handleExportFolder = useCallback(
    async (folderId: string | null) => {
      if (!folderId) return;
      const folder = findEnrichedFolder(folderId);
      if (!folder) return;
      // What the folder is called on screen — the Brief folder's zip follows the UI language.
      const name: string = folder.displayName ?? folder.name;

      toast.info(t.projects.folder.exportPreparing.replace("{name}", name));

      try {
        const { successCount } = await documentsRepo.bulkDownloadWithFolders({
          documents: [],
          folderIds: [folderId],
          zipName: name,
        });

        // successCount === 0 is a valid outcome: the folder held only empty
        // subfolders, so the zip carries directory structure but no files.
        // Interpolating "0" into exportSuccess reads as a failed no-op, so mirror
        // DocumentStore.bulkDownload and show a structure-only message instead.
        toast.success(
          successCount === 0
            ? t.projects.folder.exportStructureOnly.replace("{name}", name)
            : t.projects.folder.exportSuccess
                .replace("{count}", String(successCount))
                .replace("{name}", name)
        );
      } catch (err) {
        console.error("Folder export failed:", err);
        toast.error(t.projects.folder.exportFolderFailed);
      }
    },
    [findEnrichedFolder, t]
  );

  // Handler for Share Folder context menu action
  const handleShareFolder = useCallback((folderId: string) => {
    setShareFolderId(folderId);
    setIsShareFolderDialogOpen(true);
  }, []);

  // Get subfolders for the current folder (to display inline in document list)
  // Uses enriched folder tree so children have document counts
  const currentSubfolders = useMemo(() => {
    if (!enrichedFolderTree) return [];
    if (!selectedFolderId) {
      // At root level - show root folders
      return enrichedFolderTree;
    }
    // Inside a folder - find it in enriched tree and return its children
    const enrichedFolder = findEnrichedFolder(selectedFolderId);
    return enrichedFolder?.children || [];
  }, [enrichedFolderTree, selectedFolderId, findEnrichedFolder]);

  // Folders whose own name matches the search query (anywhere in the current
  // search scope subtree). Without this, a search like "kaust1.22" only
  // surfaces a folder that happens to be a direct child or contains a doc
  // hit — a deeply nested folder with no doc matches inside is invisible
  // (WHA-3177). At project root the scope is the whole tree; inside a folder
  // the scope is that folder's descendants — matching the WHA-3168 content
  // search scope.
  const folderNameMatches = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query || !enrichedFolderTree) return [];

    const scope = selectedFolderId
      ? (findEnrichedFolder(selectedFolderId)?.children ?? [])
      : enrichedFolderTree;

    const out: any[] = [];
    const walk = (folders: any[]) => {
      for (const f of folders) {
        const label: unknown = f.displayName ?? f.name;
        if (
          (typeof label === "string" && label.toLowerCase().includes(query)) ||
          (typeof f.name === "string" && f.name.toLowerCase().includes(query))
        ) {
          out.push(f);
        }
        if (f.children?.length) walk(f.children);
      }
    };
    walk(scope);
    return out;
  }, [searchText, enrichedFolderTree, selectedFolderId, findEnrichedFolder]);

  // Folders rendered in the table:
  //   - Browse mode (no search): direct children of the selected folder.
  //   - Search mode (WHA-3185): only folders whose own name matches the query.
  //     Folders that merely *contain* hits no longer appear; their matching
  //     files surface as direct file rows in the result list instead.
  const folderRowsForDisplay = useMemo(() => {
    if (!searchText.trim()) return currentSubfolders;
    return folderNameMatches;
  }, [currentSubfolders, folderNameMatches, searchText]);

  // ============================================================================
  // Search-mode unified row list (WHA-3185 + WHA-3183)
  //
  // When a search is active, the table renders a single ordered list of rows
  // instead of "folders block at top, then current-folder docs". Files from
  // the current folder's *subtree* surface directly as document rows, each
  // carrying a relative breadcrumb so the user can see which subfolder a hit
  // lives in. Folder rows are only the name-match folders (folderNameMatches).
  //
  // Order (per WHA-3183, when no column-header sort is active):
  //   1) filename hits   (sorted by filename, ascending)
  //   2) folder-name hits (sorted by folder name, ascending)
  //   3) content hits     (filename-disjoint by SQL contract, sorted by filename)
  //
  // When a column-header sort IS active (sortKey set), DocumentListTable's
  // default sort applies inside its own rendering — so we leave the existing
  // documents prop pipeline alone and only override layout for the no-sort
  // case via orderedSearchRows.
  // ============================================================================

  // Flat folder map for breadcrumb resolution. Walks the enriched tree once
  // and indexes each folder by its string id; resolveDocumentBreadcrumb then
  // walks parentId to build full segments.
  const folderMapForBreadcrumbs = useMemo(() => {
    const map = new Map<
      string,
      {
        name?: string;
        parentId?: string | null;
        project_id?: number | null;
        folder_path?: string | null;
      }
    >();
    const projectIdNum = numericProjectId;
    if (!enrichedFolderTree) return map;
    const walk = (folders: any[]) => {
      for (const f of folders) {
        map.set(String(f.id), {
          name: f.displayName ?? f.name,
          parentId: f.parentId !== undefined && f.parentId !== null ? String(f.parentId) : null,
          project_id: projectIdNum,
          folder_path: f.folder_path ?? null,
        });
        if (f.children?.length) walk(f.children);
      }
    };
    walk(enrichedFolderTree);
    return map;
  }, [enrichedFolderTree, numericProjectId]);

  const projectMapForBreadcrumbs = useMemo(() => {
    const map = new Map<string, { name: string }>();
    if (numericProjectId !== null) {
      map.set(String(numericProjectId), { name: projectName ?? "" });
    }
    return map;
  }, [numericProjectId, projectName]);

  // Shared row builder for the unified-row lists (search mode and browse-mode
  // tag filtering): maps a repo row to a document row carrying a breadcrumb
  // relative to the current folder.
  const toUnifiedDocRow = useCallback(
    (doc: DocumentRowMapped): UnifiedSearchRow => {
      const segments = resolveDocumentBreadcrumb(
        {
          project_id: doc.project_id ?? null,
          folder_id: doc.folder_id ?? null,
          filename: doc.filename,
        },
        projectMapForBreadcrumbs,
        folderMapForBreadcrumbs
      );
      return {
        kind: "document",
        doc: toDocumentWithTags(doc),
        breadcrumbSegments: relativeBreadcrumb(segments, selectedFolderId),
      };
    },
    [projectMapForBreadcrumbs, folderMapForBreadcrumbs, selectedFolderId]
  );

  const orderedSearchRows = useMemo<UnifiedSearchRow[] | undefined>(() => {
    const trimmed = searchText.trim();
    if (!trimmed) return undefined;

    // Same pending window as filteredDocuments: an unresolvable name-based
    // chip must not let unfiltered search hits through.
    if (isAwaitingFilterOptions) return [];

    const toRow = toUnifiedDocRow;

    // Tag filters apply to the search results too, so the rows match what
    // `filteredDocuments` (the table's data source) resolved to.
    const tagFilters = activeTagFilters ?? [];
    const inRange = (d: DocumentRowMapped) => matchesUploadDateRange(d.created_at, addedRange);
    const filenameHits = filterDocumentsByTags(
      filenameMatchDocs,
      tagFilters,
      availableFilters
    ).filter(inRange);
    const contentHits = filterDocumentsByTags(contentOnlyDocs, tagFilters, availableFilters).filter(
      inRange
    );

    // Defensive dedup: if a doc somehow shows up in both sets (the SQL
    // contract excludes filename overlap, but client-side races could),
    // prefer the filename hit and drop the duplicate from content.
    const filenameIds = new Set(filenameHits.map((d) => d.id));
    const contentDeduped = contentHits.filter((d) => !filenameIds.has(d.id));

    // When the user has the default sort active (sortKey="created_at",
    // sortOrder="desc" — the only state in which they have NOT explicitly
    // chosen a column header), preserve the tier ordering documented in
    // WHA-3183 — but with the order corrected per follow-up feedback:
    //   1. filename matches    (sorted alphabetically by filename, asc)
    //   2. content-only matches (sorted alphabetically by filename, asc)
    //   3. folder-name matches  (sorted alphabetically by name, asc)
    // When the user clicks a column header, flat-sort all three tiers by
    // their chosen column via compareUnifiedSearchRow.
    const isDefaultSort = sortKey === "created_at" && sortOrder === "desc";
    if (isDefaultSort) {
      const byFilename = (a: DocumentRowMapped, b: DocumentRowMapped) =>
        a.filename.localeCompare(b.filename);
      const byFolderName = (a: any, b: any) =>
        String(a.name ?? "").localeCompare(String(b.name ?? ""));
      const filenameRows = [...filenameHits].sort(byFilename).map(toRow);
      const contentRows = [...contentDeduped].sort(byFilename).map(toRow);
      const folderRows: UnifiedSearchRow[] = [...folderNameMatches]
        .sort(byFolderName)
        .map((f: any) => ({ kind: "folder", folder: f }));
      return [...filenameRows, ...contentRows, ...folderRows];
    }

    const rows: UnifiedSearchRow[] = [
      ...filenameHits.map(toRow),
      ...folderNameMatches.map((f: any) => ({ kind: "folder" as const, folder: f })),
      ...contentDeduped.map(toRow),
    ];
    if (sortKey && sortOrder) {
      rows.sort((a, b) => compareUnifiedSearchRow(a, b, sortKey, sortOrder));
    }
    return rows;
  }, [
    searchText,
    filenameMatchDocs,
    folderNameMatches,
    contentOnlyDocs,
    toUnifiedDocRow,
    sortKey,
    sortOrder,
    activeTagFilters,
    availableFilters,
    addedRange,
    isAwaitingFilterOptions,
  ]);

  // Browse-mode tag filtering renders through the same unified-row path as
  // search so each subtree hit shows which subfolder it lives in. Folder rows
  // stay the current folder's children (unfiltered) so navigation keeps
  // working while the filter is active; version expansion is flat here, same
  // as search mode.
  const orderedTagFilterRows = useMemo<UnifiedSearchRow[] | undefined>(() => {
    if (searchText.trim() || tagFilterDocs === null) return undefined;

    const folderRows: UnifiedSearchRow[] = currentSubfolders.map((f: TreeFolder) => ({
      kind: "folder" as const,
      folder: f,
    }));
    const isDefaultSort = sortKey === "created_at" && sortOrder === "desc";
    if (isDefaultSort) {
      // Default browse layout: folders block first, then docs by filename.
      const docRows = [...tagFilterDocs]
        .sort((a, b) => a.filename.localeCompare(b.filename))
        .map(toUnifiedDocRow);
      return [...folderRows, ...docRows];
    }
    const rows = [...folderRows, ...tagFilterDocs.map(toUnifiedDocRow)];
    if (sortKey && sortOrder) {
      rows.sort((a, b) => compareUnifiedSearchRow(a, b, sortKey, sortOrder));
    }
    return rows;
  }, [searchText, tagFilterDocs, currentSubfolders, toUnifiedDocRow, sortKey, sortOrder]);

  const visibleOrderedRows = useMemo(() => {
    const rows = orderedSearchRows ?? orderedTagFilterRows;
    if (!rows || !hideFranklinMade) return rows;
    return rows.filter((row) => row.kind !== "document" || !isFranklinMade(row.doc));
  }, [orderedSearchRows, orderedTagFilterRows, hideFranklinMade]);

  // Handle document move via junction table
  const handleMoveDocument = async (documentId: string, requestedTargetId: string | null) => {
    const targetFolderId = toViewFolder(requestedTargetId);
    // Skip no-op when user picks the same folder the doc already lives in. Avoids
    // a misleading "moved successfully" toast for an action that did nothing
    // (WHA-3261) — but say so, rather than leaving a drop with no outcome at all.
    const currentDoc = documents.find((d) => d.id === documentId);
    const currentFolderId = currentDoc?.folder_id ?? null;
    if (currentDoc && currentFolderId === targetFolderId) {
      toast.info(t.projects.folder.alreadyInFolder);
      return;
    }

    try {
      // A pinned file says why in its own toast and stays where it is.
      const [movableId] = await withoutPinnedDocuments([documentId], "move");
      if (!movableId) return;
      const userId = userProfile?.id ? String(userProfile.id) : "";
      const orgId = currentOrganization?.id || "";
      const { movedIds } = await documentsRepo.bulkUpdateFolder(
        [documentId],
        targetFolderId,
        userId,
        projectId,
        buildingId,
        orgId
      );
      // A resolved write is not a completed one: RLS filters the UPDATE, so a
      // document the user may not modify comes back unwritten and unmentioned.
      // Report that as the failure it is, and leave the row selected to retry —
      // "Document moved" over a row still sitting in its old folder is worse
      // than no move at all.
      if (movedIds.length === 0) {
        toast.error(t.projects.folder.moveDocFailed);
        return;
      }
      documentStore.selectDocument(documentId, false);
      // Invalidate cache and refetch
      documentStore.invalidateProjectCache(projectId);
      await refetchDocuments();
      toast.success(t.projects.folder.documentMoved);
    } catch (error) {
      if (notifyIfPinned(error)) return;
      toast.error(error instanceof Error ? error.message : t.projects.folder.moveDocFailed);
    }
  };

  /**
   * Repaint the list from the server after a write, reporting its own failure.
   * Callers run this from a `finally`, where a rejection would replace the
   * write's outcome — turning a completed move into "operation failed" and
   * skipping whatever the caller does next. Staying quiet instead is no better:
   * the write landed but the rows on screen are stale, which reads as "nothing
   * happened", so this offers the same retry the folder loader does.
   */
  const repaintProjectDocuments = useCallback(async () => {
    documentStore.invalidateProjectCache(projectId);
    try {
      await refetchDocuments();
    } catch (refetchError) {
      console.error("Refetch after write failed:", refetchError);
      toast.error(t.projects.folder.documentsLoadFailed, {
        action: {
          label: t.common.retry,
          onClick: () => setFolderReloadTick((tick) => tick + 1),
        },
      });
    }
  }, [documentStore, projectId, refetchDocuments, t]);

  /** Who a write from this view is attributed to, and under which org. */
  const currentWriter = () => ({
    // Guard the id, not its holder: a profile without one would otherwise
    // stringify to "undefined" and reach the write as NaN, where the sibling
    // single-document move sends the empty-string fallback.
    userId: userProfile?.id ? String(userProfile.id) : "",
    orgId: currentOrganization?.id ? String(currentOrganization.id) : "",
  });

  /**
   * The writes behind a move, in the order the repaint depends on, reporting
   * what actually landed.
   *
   * Every write here is independent, so no rejection stops the rest or makes
   * the whole gesture a failure: an RLS refusal on the documents statement
   * still leaves three movable folders to move, and a refusal on one folder
   * leaves the others. Each failure is counted and handed back, and the caller
   * reports the batch once from that.
   *
   * The documents go first only because they are one statement; the folders
   * follow in order because the repaint reads the tree from the server, so the
   * new parents have to be committed before it runs.
   */
  const commitMoveSelection = async (
    docIdsToMove: string[],
    topLevelFolders: string[],
    targetFolderId: string | null,
    writer: { userId: string; orgId: string }
  ) => {
    // One statement, but not all-or-nothing: a rejection means none of these
    // documents moved, while a resolution means only that the ones it names
    // did. RLS filters an UPDATE instead of refusing it, so a selection
    // spanning a project the user may not write comes back partly written with
    // no error at all — what the server hands back is the only account of it.
    let failedDocIds: string[] = [];
    // Kept for the report: counting a failure is what the user sees, but the
    // rejection itself is the only thing that says why, and swallowing it here
    // would leave nothing for Sentry to attach.
    const errors: unknown[] = [];
    if (docIdsToMove.length > 0) {
      try {
        const { movedIds } = await documentsRepo.bulkUpdateFolder(
          docIdsToMove,
          targetFolderId,
          writer.userId,
          projectId,
          buildingId,
          writer.orgId
        );
        const moved = new Set(movedIds);
        failedDocIds = docIdsToMove.filter((docId) => !moved.has(docId));
      } catch (error) {
        console.error("Move documents failed:", error);
        failedDocIds = docIdsToMove;
        errors.push(error);
      }
    }
    // `silent` suppresses both the per-folder "Folder moved" toast and its
    // destructive twin — the caller reports the whole selection once, from
    // what came back here.
    const failedFolderIds: string[] = [];
    for (const folderId of topLevelFolders) {
      try {
        await moveFolderAsync({ folderId, newParentId: targetFolderId, silent: true });
      } catch (error) {
        console.error("Move folder failed:", folderId, error);
        failedFolderIds.push(folderId);
        errors.push(error);
      }
    }
    return {
      moved:
        docIdsToMove.length -
        failedDocIds.length +
        (topLevelFolders.length - failedFolderIds.length),
      failed: failedDocIds.length + failedFolderIds.length,
      failedDocIds,
      failedFolderIds,
      errors,
    };
  };

  /**
   * Why this destination refuses the selection, or null when it takes it.
   *
   * Both refusals live here rather than at each caller: the drop path checks
   * them before it ever gets here (`isEligibleMoveDropTarget` is what decides a
   * row is a drop target at all), but the pickers render the tree unfiltered —
   * the selection's own descendants and the read-only system folder included —
   * so this is the only thing between them and a write no drop would make. A
   * folder's parent written to one of its own children is the worse of the two:
   * it detaches that whole subtree from the tree builder, and every row under
   * it disappears.
   */
  const moveDestinationRefusal = (
    topLevelFolders: string[],
    targetFolderId: string | null
  ): string | null => {
    const tree = folderTree ?? [];
    if (!isWritableFolderDropTarget(tree, targetFolderId)) {
      return t.projects.folder.cannotMoveIntoFolder;
    }
    if (wouldCreateCircularFolderMove(tree, topLevelFolders, targetFolderId)) {
      return t.projects.folder.cannotMoveIntoDescendant;
    }
    return null;
  };

  /**
   * Report a move the server refused, from the one place every caller's move
   * runs through.
   *
   * A failure here is counted rather than thrown — the batch carries on, and
   * the user gets one toast — so without this the refusal never leaves the
   * browser. The toast says how many items stayed put; only the report says
   * which ones, and why. An RLS filter is the case that needs it most: the
   * UPDATE resolves with the refused rows simply absent, so there is no
   * rejection to attach and the message below is the whole account of it.
   */
  const reportMoveFailureToSentry = (
    outcome: { failedDocIds: string[]; failedFolderIds: string[]; errors: unknown[] },
    targetFolderId: string | null
  ) => {
    const [firstError] = outcome.errors;
    Sentry.captureException(moveFailureError(firstError), {
      tags: { feature: "project-move-copy", mode: "move" },
      extra: {
        sourceProjectId: projectId,
        targetFolderId,
        buildingId,
        failedDocIds: outcome.failedDocIds,
        failedFolderIds: outcome.failedFolderIds,
        failedDocCount: outcome.failedDocIds.length,
        failedFolderCount: outcome.failedFolderIds.length,
        // Only the first is the exception; a batch can fail on several
        // writes for several reasons, and the rest are lost without this.
        errors: outcome.errors.map(describeMoveError),
        userId: userProfile?.id,
        orgId: currentOrganization?.id,
      },
    });
  };

  /**
   * Returns what the move wrote, or null when there was nothing to write — so a
   * no-op reports nothing, and a partial move is never reported as a success.
   */
  const performMoveSelection = async (
    docIds: string[],
    folderIds: string[],
    targetFolderId: string | null
  ) => {
    const topLevelFolders = getTopLevelFolderIds(folderIds, findFolderById);
    const refusal = moveDestinationRefusal(topLevelFolders, targetFolderId);
    if (refusal) {
      toast.error(refusal);
      return null;
    }
    // Exclusion is computed against every selected folder, including the ones
    // that turn out to be no-ops below: a doc inside a folder that stays put
    // stays with it, rather than being torn out into the target on its own.
    const docsInSubtrees = await excludeDocsInFolderSubtrees(docIds, topLevelFolders);
    // Documents already sitting in the target are dropped for the same reason
    // the folders are: writing `folder_id` to the value it already holds is not
    // a move, and reporting it as "Items moved" is the misleading success
    // `handleMoveDocument` refuses (WHA-3261). A document the current page
    // doesn't carry is kept — an unknown parent is no evidence of a no-op.
    const docIdsOutsideTarget = docsInSubtrees.filter((docId) => {
      const doc = documents.find((d) => d.id === docId);
      return !doc || (doc.folder_id ?? null) !== targetFolderId;
    });
    // Pinned files (Issue Attachments, the brief's Additions PDF) stay, each
    // named in its own toast. They are left out before the write because the
    // database refuses a statement that names even one of them.
    const docIdsToMove = await withoutPinnedDocuments(docIdsOutsideTarget, "move");
    const pinnedCount = docIdsOutsideTarget.length - docIdsToMove.length;
    const foldersToMove = topLevelFolders.filter(
      (folderId) => !isFolderAlreadyIn(folderId, targetFolderId)
    );
    if (docIdsToMove.length === 0 && foldersToMove.length === 0) {
      // Everything left was pinned, and each one has already said so.
      if (pinnedCount > 0) return null;
      // Every drop target is advertised while a move is in flight, including the
      // one the selection already lives in; a drop there has to end in a word.
      toast.info(t.projects.folder.alreadyInFolder);
      return null;
    }

    let outcome: {
      moved: number;
      failed: number;
      failedDocIds: string[];
      failedFolderIds: string[];
      errors: unknown[];
    };
    try {
      outcome = await commitMoveSelection(
        docIdsToMove,
        foldersToMove,
        targetFolderId,
        currentWriter()
      );
    } finally {
      // A move that fails halfway still committed everything before the
      // failure, so the list has to be repainted either way — otherwise the
      // user is told the operation failed while rows sit in their old place.
      await repaintProjectDocuments();
    }
    // A file pinned after the check refuses the whole documents statement:
    // name it rather than leave only "operation failed".
    outcome.errors.forEach((error) => notifyIfPinned(error));
    if (outcome.failed > 0) reportMoveFailureToSentry(outcome, targetFolderId);
    // What refused to move stays selected, ready to retry — the affordance the
    // single-folder move keeps for the same reason.
    documentStore.clearSelection();
    outcome.failedDocIds.forEach((docId) => documentStore.selectDocument(docId, true));
    setSelectedFolderIds(outcome.failedFolderIds);
    return outcome;
  };

  /**
   * The one report for a batch move. A rejection partway through leaves the
   * writes before it committed and repainted on screen, so "operation failed"
   * would contradict what the user is looking at.
   */
  const reportMoveOutcome = (outcome: { moved: number; failed: number }) => {
    const failed = outcome.failed;
    if (failed === 0) {
      toast.success(t.projects.folder.itemsMoved);
    } else if (outcome.moved === 0) {
      toast.error(t.projects.folder.operationFailed);
    } else {
      toast.warning(
        t.projects.folder.itemsPartiallyMoved
          .replace("{moved}", String(outcome.moved))
          .replace("{failed}", String(failed))
      );
    }
  };

  const handleMoveSelection = async (
    docIds: string[],
    folderIds: string[],
    requestedTargetId: string | null
  ) => {
    const targetFolderId = toViewFolder(requestedTargetId);
    if (!canReorganizeProject) return;
    if (docIds.length === 0 && folderIds.length === 0) return;

    try {
      const outcome = await performMoveSelection(docIds, folderIds, targetFolderId);
      if (outcome) reportMoveOutcome(outcome);
    } catch (error) {
      console.error("Move selection failed:", error);
      toast.error(t.projects.folder.operationFailed);
    }
  };

  const handleSoftDeleteDocument = async (document: Document) => {
    // Move document to trash
    const success = await trashOperations.handleMoveToTrash(document);
    if (success) {
      // After moving to trash, refresh documents
      await refetchDocuments();
    }
  };

  const handlePermanentDeleteDocument = (document: Document): void => {
    const projectIdNum = Number(projectId);
    const canDelete =
      hasPermission(PERMISSIONS.DELETE_DATA) ||
      (projectIdNum &&
        hasPermission(PERMISSIONS.DELETE_PROJECT_FILES, { projectId: projectIdNum }));

    if (!canDelete || !selectedFolder?.isCustom) {
      toastUi({
        variant: "destructive",
        title: t.projects.folder.permissionDenied,
        description: t.projects.folder.permissionDeniedDesc,
      });
      return;
    }

    setDocumentToDelete(document);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmPermanentDelete = async (): Promise<void> => {
    if (!documentToDelete) return;

    try {
      const success = await permanentlyDeleteDocument(documentToDelete);
      if (success) {
        toastUi({
          title: t.projects.folder.documentPermanentlyDeleted,
          description: t.projects.folder.documentPermanentlyDeletedDesc.replace(
            "{filename}",
            documentToDelete.filename
          ),
        });
      }
    } catch (error) {
      toastUi({
        variant: "destructive",
        title: t.projects.folder.permanentDeleteFailed,
        description: t.projects.folder.permanentDeleteFailedDesc,
      });
    } finally {
      setDocumentToDelete(null);
      setIsDeleteConfirmOpen(false);
      await refetchDocuments();
    }
  };

  // Every upload the Upload dialog or the page-level drop zone makes lands
  // here. The open folder comes from the URL, so it has to be a confirmed
  // target (see `isFolderTargetConfirmed`); a folder picked in the dialog's
  // destination picker is a loaded row and needs no such check.
  const uploadToFolder = async (selection: UploadSelection, folderId: string | null) => {
    if (lacksAddPermission) {
      refuseAdd();
      return;
    }
    if (folderId === selectedFolderId && folderTargetBlockedReason) {
      toast.error(folderTargetBlockedReason);
      return;
    }
    try {
      // All uploads go through the queue system. The selection's folder tree
      // is created under the target before any file is queued.
      await handleBulkUpload(
        selection.files,
        selection.relativePaths,
        projectId,
        folderId ?? undefined,
        selection.directoryPaths
      );
    } catch (error) {
      console.error("Error in uploadToFolder:", error);
      toast.error(t.projects.folder.failedUpload);
    }
  };

  const handleFileDrop = (selection: UploadSelection) =>
    uploadToFolder(selection, selectedFolderId);

  // OS files released directly on a folder target (tree row, breadcrumb
  // segment, root header) upload into THAT folder — not into the open folder
  // the page-level zone below targets. Container pseudo-folders and the
  // system folder never accept these drops (gated inside useFolderDragDrop).
  const handleFileDropOnFolder = (selection: UploadSelection, requestedTargetId: string | null) => {
    if (lacksAddPermission) {
      refuseAdd();
      return;
    }
    const targetFolderId = toViewFolder(requestedTargetId);
    handleBulkUpload(
      selection.files,
      selection.relativePaths,
      projectId,
      targetFolderId ?? undefined,
      selection.directoryPaths
    ).catch((error) => {
      console.error("Error in handleFileDropOnFolder:", error);
      toast.error(t.projects.folder.failedUpload);
    });
  };

  // Page-level external-file drop. Uploads dropped OS files straight into the
  // currently open folder/subfolder (selectedFolderId) — the same target as the
  // "Upload" button. Disabled inside the system Issue Attachments folder, where
  // the Upload button is likewise locked. Activates only for external file
  // drags, so it never interferes with in-app drag-to-move of docs/folders.
  const { isDragOver: isFileDragOver, dropZoneProps: fileDropZoneProps } = useExternalFileDrop({
    onFiles: handleFileDrop,
    // Not disabled for a member who may not add: the drop is refused with a
    // message in uploadToFolder instead of silently doing nothing.
    disabled: isSystemFolder(selectedFolder),
  });

  const handleSelectionChange = (_docIds: string[], folderIds: string[]) => {
    // Document selection is handled by the store via DocumentListTable
    // Only handle folder selection here
    setSelectedFolderIds(folderIds);
  };

  // Checking a folder selects every descendant folder + every latest-version doc
  // inside the subtree. Unchecking removes the same set. Subtrees may contain
  // items the user can't see (subfolders not navigated into) — the cascade keeps
  // bulk-action selection state truthful across the whole tree, so future
  // bulk actions don't each need their own recursion logic. Operations that
  // already recurse internally (move/copy/trash on a folder) filter the
  // selection back down to the top-level folders before iterating; see
  // `getTopLevelFolderIds` and `excludeDocsInFolderSubtrees` usages below.
  const handleFolderCheckboxToggle = useCallback(
    async (folderId: string, isSelected: boolean) => {
      // Reflect the clicked folder's own selection state immediately — the
      // cascade into descendant docs is async, but the folder row's selected
      // styling (and the bulk bar appearing) shouldn't wait on the network.
      if (isSelected) {
        setSelectedFolderIds((prev) => Array.from(new Set([...prev, folderId])));
      } else {
        setSelectedFolderIds((prev) => prev.filter((id) => id !== folderId));
      }

      // Mark the cascade in flight so the bulk bar shows a loading count with
      // its actions disabled until every descendant is selected.
      setPendingCascadeCount((n) => n + 1);
      try {
        let subtree: { folderIds: string[]; docIds: string[] };
        try {
          subtree = await fetchFolderSubtree(folderId);
        } catch (error) {
          console.error("Failed to fetch folder subtree for selection cascade:", error);
          // The folder itself is already (de)selected above, so the click
          // isn't a no-op — just skip the unavailable descendant cascade.
          subtree = { folderIds: [], docIds: [] };
        }

        if (isSelected) {
          setSelectedFolderIds((prev) =>
            Array.from(new Set([...prev, folderId, ...subtree.folderIds]))
          );
          for (const id of subtree.docIds) {
            documentStore.selectDocument(id, true);
          }
        } else {
          const drop = new Set([folderId, ...subtree.folderIds]);
          setSelectedFolderIds((prev) => prev.filter((id) => !drop.has(id)));
          for (const id of subtree.docIds) {
            documentStore.selectDocument(id, false);
          }
        }
      } finally {
        setPendingCascadeCount((n) => Math.max(0, n - 1));
      }
    },
    [documentStore]
  );

  const handleBulkMove = () => {
    setBulkAction("move");
    setIsFolderPickerOpen(true);
  };

  const handleBulkCopy = () => {
    setBulkAction("copy");
    setIsFolderPickerOpen(true);
  };

  const handleBulkNewProject = () => {
    setBulkAction("newProject");
    setIsProjectModalOpen(true);
  };

  const handleFolderSelected = async (requestedTargetId: string | null) => {
    const targetFolderId = toViewFolder(requestedTargetId);
    if (!bulkAction || (!selectedDocIds.length && !selectedFolderIds.length)) return;

    try {
      if (bulkAction === "move") {
        // Same move the drop target performs — the circular-move and
        // unwritable-destination refusals, cascade handling, refetch, reporting
        // and the selection reset all live in one place. The picker offers the
        // whole tree, this selection's own descendants and the read-only system
        // folder included, so it depends on those refusals in a way the drop
        // path does not: the drag never advertises an ineligible row. Nothing is
        // cleared here on top of it: a folder that refused to move stays
        // selected so the user can retry it.
        const outcome = await performMoveSelection(
          selectedDocIds,
          selectedFolderIds,
          targetFolderId
        );
        if (outcome) reportMoveOutcome(outcome);
      } else if (bulkAction === "copy") {
        // Copy is not supported - documents can only exist in one folder at a time
        toast.error(t.projects.folder.copyToFolderNotSupported);
      }
    } catch (error) {
      console.error("Bulk operation failed:", error);
      toast.error(t.projects.folder.operationFailed);
    }
  };

  const handleProjectSelected = async (project: BuildingProject) => {
    const selectedProjectId = String(project.id);
    if (!selectedProjectId || (!selectedDocIds.length && !selectedFolderIds.length)) return;

    try {
      let copied = 0;
      let failed = 0;
      let failedDocIds: string[] = [];
      let quotaFailed = 0;
      let quotaFailureMessage: string | null = null;
      // Copy documents to new project
      if (selectedDocIds.length > 0) {
        const copyResult = await projectCopyService.copyDocumentsToOtherProject({
          documentIds: selectedDocIds,
          sourceProjectId: projectId,
          targetProjectId: selectedProjectId,
          targetBuildingId: buildingId,
          orgId: currentOrganization?.id || "",
          userId: String(userProfile?.id || ""),
          targetFolderId: null,
        });
        copied = copyResult.copied;
        failed = copyResult.failed;
        failedDocIds = copyResult.failedIds;
        quotaFailed = copyResult.quotaFailed;
        quotaFailureMessage = copyResult.quotaFailureMessage;
      }

      // Note: Folder copying to new projects has been disabled
      if (selectedFolderIds.length > 0) {
        toast.error(t.projects.folder.folderCopyNotSupported);
      }

      if (copied > 0) toast.success(`Copied ${copied} document(s)`);
      if (quotaFailed > 0 && quotaFailureMessage) toast.error(quotaFailureMessage);
      if (failed > quotaFailed) toast.error(t.projects.folder.failedCopyItems);
      const failedSet = new Set(failedDocIds);
      selectedDocIds
        .filter((id) => !failedSet.has(id))
        .forEach((id) => documentStore.selectDocument(id, false));
      if (failed === 0 && selectedFolderIds.length === 0) {
        documentStore.clearSelection();
        setSelectedFolderIds([]);
      }
    } catch (error) {
      const expectedError =
        error instanceof BuildingStorageQuotaError
          ? error
          : error && typeof error === "object" && "message" in error
            ? (storageQuotaErrorFrom(error as { code?: string; message?: string }) ??
              storageAccessErrorFrom(error as { code?: string; message?: string }))
            : null;
      if (!expectedError) {
        console.error("Project copy failed:", error);
      }
      toast.error(expectedError?.message ?? t.projects.folder.failedCopyItems);
    }
  };

  const handleClearSelection = () => {
    documentStore.clearSelection();
    setSelectedFolderIds([]);
  };

  // Handler for move/copy
  const handleMoveCopy = () => {
    setIsProjectDestinationOpen(true);
  };

  // Handler for "Ask Franklin" bulk action
  const handleAskFranklin = useCallback(
    (docs: Array<{ id: string; filename: string }>) => {
      docs.forEach((d) => chatStore.addAttachedDocument(d));
      chatStore.setViewMode("sidebar");
      documentStore.clearSelection();
    },
    [documentStore, chatStore]
  );

  const handleProjectDestinationConfirm = async (result: {
    projectId: string;
    folderId: string | null;
    mode: ProjectDestinationMode;
  }) => {
    console.log("[Move/Copy] handleProjectDestinationConfirm called with:", {
      mode: result.mode,
      targetProjectId: result.projectId,
      targetFolderId: result.folderId,
      sourceProjectId: projectId,
      selectedDocIds,
      selectedFolderIds,
    });

    if (selectedDocIds.length === 0 && selectedFolderIds.length === 0) {
      toast.error(t.projects.folder.noItemsSelected);
      setIsProjectDestinationOpen(false);
      return;
    }

    const isSameProject = result.projectId === projectId;

    setIsProjectOperationLoading(true);
    try {
      const userId = userProfile?.id ? String(userProfile.id) : "";
      const orgId = currentOrganization?.id || "";

      console.log("[Move/Copy] isSameProject:", isSameProject, "mode:", result.mode);

      // A move that stays in this project is the move the drop targets and the
      // "Move to folder" picker already perform, so it runs through the one
      // implementation instead of a second copy of it: the circular-move and
      // writable-destination refusals, the no-op filtering, the per-folder
      // failure count, the repaint and the selection reset all live there. This
      // modal needs those refusals more than any other caller — it renders the
      // target project's whole tree with nothing excluded, so with the target
      // project set to this one a descendant of a selected folder is a click
      // away.
      if (isSameProject && result.mode === "move") {
        const outcome = await performMoveSelection(
          selectedDocIds,
          selectedFolderIds,
          result.folderId
        );
        // A refused destination has already said why; the modal stays open on
        // the tree so the next click can pick another folder.
        if (outcome) {
          reportMoveOutcome(outcome);
          setIsProjectDestinationOpen(false);
        }
        return;
      }

      let movedDocs = 0;
      let movedFolders = 0;
      let failedDocs = 0;
      let failedDocIds: string[] = [];
      const completedFolderIds: string[] = [];
      let quotaFailedDocs = 0;
      let quotaFailureMessage: string | null = null;

      // Cascade selection includes descendants. Folder operations recurse
      // internally, so iterate only top-level folders, and strip docs that
      // already sit inside one of those folders to avoid double-handling.
      const topLevelFolderIds = getTopLevelFolderIds(selectedFolderIds, findFolderById);
      const docIdsToHandle = await excludeDocsInFolderSubtrees(selectedDocIds, topLevelFolderIds);

      // Handle documents
      if (docIdsToHandle.length > 0) {
        if (result.mode === "move") {
          // Pinned files (Issue Attachments, the brief's Additions PDF) stay,
          // each named in its own toast; the rest of the selection moves.
          const movableDocIds = await withoutPinnedDocuments(docIdsToHandle, "move");
          console.log("[Move/Copy] Calling moveDocumentsToOtherProject");
          await projectCopyService.moveDocumentsToOtherProject({
            documentIds: movableDocIds,
            sourceProjectId: projectId,
            targetProjectId: result.projectId,
            targetBuildingId: buildingId,
            orgId,
            userId,
            targetFolderId: result.folderId,
          });
          movedDocs += movableDocIds.length;
        } else {
          console.log("[Move/Copy] Copying documents");
          const copyResult = await projectCopyService.copyDocumentsToOtherProject({
            documentIds: docIdsToHandle,
            sourceProjectId: projectId, // Pass source for same-project copy handling
            targetProjectId: result.projectId,
            targetBuildingId: buildingId,
            orgId,
            userId,
            targetFolderId: result.folderId,
          });
          movedDocs += copyResult.copied;
          failedDocs += copyResult.failed;
          failedDocIds = failedDocIds.concat(copyResult.failedIds);
          quotaFailedDocs += copyResult.quotaFailed;
          quotaFailureMessage ??= copyResult.quotaFailureMessage;
        }
      }

      // Handle folders
      if (topLevelFolderIds.length > 0) {
        for (const folderId of topLevelFolderIds) {
          if (result.mode === "move") {
            await projectCopyService.moveFolderToOtherProject({
              sourceFolderId: folderId,
              sourceProjectId: projectId,
              sourceBuildingId: buildingId,
              targetProjectId: result.projectId,
              targetBuildingId: buildingId,
              targetParentFolderId: result.folderId,
              orgId,
              userId,
            });
          } else {
            const copyResult = await projectCopyService.copyFolderToOtherProject({
              sourceFolderId: folderId,
              sourceProjectId: projectId,
              sourceBuildingId: buildingId,
              targetProjectId: result.projectId,
              targetBuildingId: buildingId,
              targetParentFolderId: result.folderId,
              orgId,
              userId,
            });
            movedDocs += copyResult.copiedDocs;
            movedFolders += copyResult.copiedFolders;
            failedDocs += copyResult.failedDocs;
            failedDocIds = failedDocIds.concat(copyResult.failedDocIds);
            if (copyResult.failedDocs === 0) completedFolderIds.push(folderId);
            quotaFailedDocs += copyResult.quotaFailedDocs;
            quotaFailureMessage ??= copyResult.quotaFailureMessage;
          }
        }
        if (result.mode === "move") movedFolders += topLevelFolderIds.length;
      }

      // Show success message
      const action = result.mode === "move" ? "Moved" : "Copied";
      const parts = [];
      if (movedDocs > 0) parts.push(`${movedDocs} document(s)`);
      if (movedFolders > 0) parts.push(`${movedFolders} folder(s)`);
      if (parts.length > 0) toast.success(`${action} ${parts.join(" and ")}`);
      if (quotaFailedDocs > 0 && quotaFailureMessage) toast.error(quotaFailureMessage);
      if (failedDocs > quotaFailedDocs) toast.error(t.projects.folder.failedCopyItems);

      // Refresh and clear selection - invalidate BOTH source and target project caches
      documentStore.invalidateProjectCache(projectId);
      if (!isSameProject) {
        documentStore.invalidateProjectCache(result.projectId);
      }
      await refetchDocuments();
      await refetchFolders();
      documentStore.loadFolderDocumentCounts(projectId, buildingId);
      if (failedDocs === 0) {
        documentStore.clearSelection();
        setSelectedFolderIds([]);
        setIsProjectDestinationOpen(false);
      } else if (result.mode === "copy") {
        const failedSet = new Set(failedDocIds);
        docIdsToHandle
          .filter((id) => !failedSet.has(id))
          .forEach((id) => documentStore.selectDocument(id, false));
        setSelectedFolderIds((current) => current.filter((id) => !completedFolderIds.includes(id)));
      }
    } catch (error) {
      if (notifyIfPinned(error)) {
        documentStore.invalidateProjectCache(result.projectId);
        await repaintProjectDocuments();
        return;
      }
      const context = {
        mode: result.mode,
        sourceProjectId: projectId,
        targetProjectId: result.projectId,
        targetFolderId: result.folderId,
        buildingId,
        selectedDocCount: selectedDocIds.length,
        selectedFolderCount: selectedFolderIds.length,
        selectedDocIds,
        selectedFolderIds,
        userId: userProfile?.id,
        orgId: currentOrganization?.id,
      };
      const quotaError =
        error instanceof BuildingStorageQuotaError
          ? error
          : error && typeof error === "object" && "message" in error
            ? (storageQuotaErrorFrom(error as { code?: string; message?: string }) ??
              storageAccessErrorFrom(error as { code?: string; message?: string }))
            : null;
      if (!quotaError) {
        console.error("Project operation failed:", context, error);
        Sentry.captureException(
          error instanceof Error
            ? error
            : new Error(String((error as { message?: string })?.message ?? error)),
          {
            tags: {
              feature: "project-move-copy",
              mode: result.mode,
            },
            extra: context,
          }
        );
      }
      toast.error(
        quotaError?.message ??
          (result.mode === "move"
            ? t.projects.folder.failedMoveItems
            : t.projects.folder.failedCopyItems)
      );
      // Each document batch and each folder above is its own write, so a
      // rejection on one leaves the ones before it committed. Without a repaint
      // here they sit in the list under their old parent — the user is told the
      // operation failed while looking at rows that did move.
      documentStore.invalidateProjectCache(result.projectId);
      await repaintProjectDocuments();
      await refetchFolders();
    } finally {
      setIsProjectOperationLoading(false);
    }
  };

  // Hard-delete from trash is gated on the org-level DELETE_DATA permission or
  // this project's DELETE_PROJECT_FILES grant (the same check the per-folder
  // permanent delete uses, minus the custom-folder constraint that only applies
  // to the main folder view).
  const projectIdNumForTrash = Number(projectId);
  const canPermanentlyDeleteInTrash =
    hasPermission(PERMISSIONS.DELETE_DATA) ||
    (!!projectIdNumForTrash &&
      hasPermission(PERMISSIONS.DELETE_PROJECT_FILES, { projectId: projectIdNumForTrash }));

  // The open folder's create actions. The header buttons and the background
  // right-click menu both render from these, so their gates cannot drift.
  const newFolderAction: FolderViewCreateAction = {
    // Same gate as the tree's create actions: no new folders inside the system
    // Issue Attachments folder — plus the unconfirmed-target gate, since this
    // is the one create path whose parent comes from the URL rather than from
    // a row in the loaded tree.
    blockedReason: isSystemFolder(selectedFolder)
      ? t.pm.issues.detail.systemFolderLocked
      : folderTargetBlockedReason,
    // Create inside the folder the user has navigated into (root when none),
    // mirroring "Create Subfolder".
    run: () => (lacksAddFolderPermission ? refuseAdd() : openCreateFolderDialog(selectedFolderId)),
  };
  // Filing a form is a write into the open folder, same as an upload — the
  // flow is handed `selectedFolderId` as a fixed destination, so it needs the
  // same confirmed target.
  const newDocumentAction: FolderViewCreateAction | null =
    buildingName != null
      ? {
          blockedReason: folderTargetBlockedReason,
          run: () => (lacksAddPermission ? refuseAdd() : setIsNewDocumentModalOpen(true)),
        }
      : null;

  // The folder path: Up (narrow, no tree) and the breadcrumb. Rendered over the
  // table, or into the host's `trailSlot`.
  const trailContent = (
    <>
      {/* Rooted and at the root, there is nowhere further back. */}
      {((selectedFolder && selectedFolderId !== viewRootId) || containerStack.length > 0) &&
        hideProjectName &&
        !treeShown && (
          // The trail lists folders (and any container drilled into)
          // only — the project is already named by the page breadcrumb
          // and at the top of the tree, so repeating it here read as a
          // duplicate. Root navigation therefore lives in the tree's
          // root item; once the aside drops below its @[1080px]
          // breakpoint this button takes over as the way back to
          // project root.
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 flex-shrink-0 px-2 text-slate-600 hover:text-slate-900",
              activeMove && canReorganizeProject && "bg-blue-50/40 ring-1 ring-blue-200",
              rootDragDrop.isDraggedOver("root") && "bg-blue-100 ring-2 ring-blue-500"
            )}
            onClick={() => handleFolderSelect(null)}
            aria-label={t.projects.folder.backToProjectRoot}
            title={t.projects.folder.backToProjectRoot}
            // In this configuration the header row that owns the root
            // drop handlers is empty and zero-height and the tree (with
            // its root item) is hidden, so this button is also the only
            // "drop back to project root" target.
            onDragOver={rootDragDrop.handleDragOver(null)}
            onDragEnter={rootDragDrop.handleDragEnter("root", false)}
            onDragLeave={rootDragDrop.handleDragLeave}
            onDrop={rootDragDrop.handleDrop(null)}
          >
            <CornerLeftUp className="h-4 w-4" />
          </Button>
        )}
      {((selectedFolder && !atViewRoot) || containerStack.length > 0) && (
        <FolderBreadcrumb
          currentFolder={selectedFolder}
          folders={trailFolders}
          onNavigate={handleFolderSelect}
          // Containers continue this trail instead of stacking a
          // second breadcrumb row under it.
          containerSegments={containerStack.map((entry) => ({
            id: entry.id,
            name: entry.filename,
          }))}
          onNavigateContainer={handleExitContainer}
          // The leading chevron reads as a continuation of the root
          // label at the top of the tree beside the trail. Without
          // that adjacent label (narrow /documents, where the root
          // button above opens the row instead, or /projects/:id
          // where the heading sits in the header row) it would point
          // at nothing, so the trail starts flush.
          leadingSeparator={hideProjectName && treeShown}
          activeMove={activeMove}
          onDropDocument={canReorganizeProject ? handleMoveDocument : undefined}
          onMoveFolder={canReorganizeProject ? handleMoveFolder : undefined}
          onDropSelection={canReorganizeProject ? handleMoveSelection : undefined}
          onDropFiles={handleFileDropOnFolder}
        />
      )}
    </>
  );

  return (
    // min-w-0 flex-1: on /projects/:id the section is the sole child of a flex
    // row and must grow to fill it (inert under the block-layout /documents
    // page). No top margin — the mounting page's space-y already separates it.
    <section className="min-w-0 flex-1">
      {showTrash ? (
        <TrashView
          trashedDocuments={trashOperations.trashedDocuments}
          onRestore={async (document) => {
            await trashOperations.handleRestoreDocument(document);
          }}
          onPermanentDelete={async (document) => {
            await trashOperations.handlePermanentlyDelete(document);
          }}
          onBulkPermanentDelete={async (documents) => {
            await trashOperations.handleBulkPermanentlyDelete(documents);
          }}
          onBulkRestore={async (documents) => {
            await trashOperations.handleBulkRestore(documents);
          }}
          onEmptyTrash={async () => {
            await trashOperations.handleEmptyTrash();
          }}
          canPermanentlyDelete={canPermanentlyDeleteInTrash}
          onPermanentDeleteDenied={
            projectIdNumForTrash
              ? () => void denyPermission("deleteFiles", projectIdNumForTrash)
              : undefined
          }
          isLoading={trashOperations.isLoading}
          onBack={() => setShowTrash(false)}
        />
      ) : (
        <div
          ref={folderViewContainerRef}
          className={cn(
            // @container is load-bearing: the aside's @[1080px] breakpoint
            // queries it, and the isFolderTreeVisible observer measures it.
            // No card border — content sits on the page background to match
            // /documents.
            "rounded-lg transition-colors @container",
            isFileDragOver && "bg-primary/5 ring-2 ring-inset ring-primary"
          )}
          {...fileDropZoneProps}
        >
          {/* Header: one row — project name on the left, search + actions
              pushed right (field styling matching the Timeline/List toolbar in
              DocumentsMainView). The folder path is NOT here: it belongs to the
              table it navigates, so it renders above the table column below.
              Wraps onto a second line when the container is narrow. */}
          <div
            className={cn(
              "flex flex-wrap items-center gap-x-4 gap-y-2",
              // On /documents the project name, inline search and actions all
              // live elsewhere, leaving this row empty — drop its margin so it
              // adds no gap; otherwise keep the spacing above the tree/table.
              hideProjectName && (hideInlineSearch || searchSlot !== undefined) ? "mb-0" : "mb-3",
              activeMove && canReorganizeProject && "rounded-md bg-blue-50/40 ring-1 ring-blue-200",
              rootDragDrop.isDraggedOver("root") && "rounded-md bg-blue-100 ring-2 ring-blue-500"
            )}
            onDragOver={rootDragDrop.handleDragOver(null)}
            onDragEnter={rootDragDrop.handleDragEnter("root", false)}
            onDragLeave={rootDragDrop.handleDragLeave}
            onDrop={rootDragDrop.handleDrop(null)}
          >
            {/* Project name — prominent, and the click target for "back to
                project root". Dropped when the host already shows the project
                in a page-level breadcrumb (/documents); the tree's root item
                carries root navigation there. */}
            {!hideProjectName && (
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <h3
                  className="min-w-0 cursor-pointer text-lg font-semibold text-slate-900 hover:text-slate-700"
                  onClick={() => handleFolderSelect(null)}
                >
                  {projectName || "Project"}
                </h3>
                {/* Only /projects/:id shows the name; every other host has its own titled page. */}
                <PageInfoButton page="projectFolders" projectId={Number.parseInt(projectId, 10)} />
              </div>
            )}
            {/* Upload + the overflow menu are page actions, so they render into
                the host page's title row when it offers a slot (/documents) and
                inline here otherwise (/projects/:id). Search sits here too —
                unless the host owns a page-level tools row (hideInlineSearch),
                in which case its box drives the shared filters context. */}
            {/* Wraps rather than overflows: with labels on New folder / New
                document this cluster is ~640px wide, and the folder tree is
                already hidden below @[1080px], so the container does get
                narrower than that. */}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {!hideInlineSearch && searchSlot === undefined && searchField}
              {!hideInlineSearch && searchSlot && createPortal(searchField, searchSlot)}
              <PageHeaderActions>
                {addExisting ? (
                  // Two ways to add files, behind one primary button: upload new
                  // ones, or pick documents already in the project.
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="control"
                        className="gap-1.5"
                        {...pageInfoTarget("files.upload")}
                      >
                        <Upload />
                        {addExisting.menuLabel}
                        <ChevronDown className="size-3.5" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        // Same guards as the plain Upload button below.
                        disabled={isSystemFolder(selectedFolder) || !isFolderTargetConfirmed}
                        onSelect={() => {
                          if (lacksAddPermission) {
                            refuseAdd();
                            return;
                          }
                          refetchDocuments();
                          setIsUploadModalOpen(true);
                        }}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {t.projects.folder.upload}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={addExisting.onSelect}>
                        <FolderInput className="mr-2 h-4 w-4" />
                        {addExisting.label}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    size="control"
                    className="gap-1.5"
                    // WHA-3270: disabled inside the system Issue Attachments folder —
                    // files there are added from the issues page only. Also while
                    // the open folder id is still unconfirmed, so the modal can't
                    // be opened against a target that may not be in this project.
                    disabled={isSystemFolder(selectedFolder) || !isFolderTargetConfirmed}
                    title={
                      isSystemFolder(selectedFolder)
                        ? t.pm.issues.detail.systemFolderLocked
                        : folderTargetBlockedReason
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (lacksAddPermission) {
                        refuseAdd();
                        return;
                      }
                      // Invalidate cache and fetch fresh data before showing modal
                      refetchDocuments();
                      setIsUploadModalOpen(true);
                    }}
                    {...pageInfoTarget("files.upload")}
                  >
                    <Upload />
                    {t.projects.folder.upload}
                  </Button>
                )}
                {/* New folder + New document sit directly left of the overflow
                    menu. The Timeline and List tabs render the same labeled
                    New document button; New folder is the Folders tab's alone. */}
                <HeaderActionIconButton
                  label={t.projects.folder.newFolder}
                  icon={<CreateGlyph icon={Folder} />}
                  showLabel
                  disabled={newFolderAction.blockedReason !== undefined}
                  disabledReason={newFolderAction.blockedReason}
                  onClick={newFolderAction.run}
                  infoTarget="files.newFolder"
                />
                {newDocumentAction && (
                  <HeaderActionIconButton
                    ref={newDocumentButtonRef}
                    label={t.documents.newDocument.trigger}
                    icon={<CreateGlyph icon={FileText} />}
                    showLabel
                    disabled={newDocumentAction.blockedReason !== undefined}
                    disabledReason={newDocumentAction.blockedReason}
                    onClick={newDocumentAction.run}
                    infoTarget="files.newDocument"
                  />
                )}
                {/* AI organise (WHA-3654) — a selected folder organises that
                    folder; no selection organises the PROJECT ROOT (loose
                    files). Same confirmed-target gate as New folder: the
                    target comes from the URL, not from a loaded row. */}
                {canOrganizeFolder && !rootFolder && (
                  <HeaderActionIconButton
                    label={
                      organizeRunning
                        ? t.documents.organize.inProgressPill
                        : organizeAwaitingReview
                          ? t.documents.organize.reviewPill
                          : t.documents.organize.button
                    }
                    icon={
                      organizeRunning ? (
                        // Franklin's own mark, turning. The static logo is four
                        // polygons in a 4-fold rotationally symmetric pinwheel,
                        // so a continuous spin is seamless — unlike the GIF the
                        // dialog shows, which is not a rotation at all. Sized
                        // explicitly because the button's [&_svg]:size-4 rule
                        // does not reach an <img>.
                        <FranklinLogo
                          size="xs"
                          showBorder={false}
                          alt=""
                          className="h-4 w-4"
                          imageClassName="h-4 w-4 animate-spin motion-reduce:animate-none"
                        />
                      ) : (
                        <Sparkles />
                      )
                    }
                    // A finished run the user has to come back to reads as
                    // nothing at all in outline, beside two other outline
                    // controls. Red is the only state here that is asking to be
                    // acted on rather than merely available.
                    variant={organizeAwaitingReview ? "default" : "outline"}
                    showLabel
                    infoTarget="files.organise"
                    // Inside a .zip/.asice the list shows the container's
                    // members, not a folder's contents — organising would run
                    // on the folder BEHIND the container, which is not what the
                    // user is looking at. The context menu drops the action for
                    // the same reason.
                    disabled={
                      isViewingContainer ||
                      isSystemFolder(selectedFolder) ||
                      !isFolderTargetConfirmed
                    }
                    disabledReason={
                      isViewingContainer
                        ? t.documents.organize.containerBlocked
                        : isSystemFolder(selectedFolder)
                          ? t.pm.issues.detail.systemFolderLocked
                          : folderTargetBlockedReason
                    }
                    onClick={() => {
                      if (selectedFolderId) {
                        handleOrganizeFolder(selectedFolderId);
                      } else {
                        setOrganizeTarget({ id: null, name: projectName ?? "" });
                        setIsOrganizeOpen(true);
                      }
                    }}
                  />
                )}
                {/* Overflow menu — everything but Upload. Trash opens this
                    view's per-project trash. */}
                {/* A rooted view has no trash of its own (it is the project's),
                    so without shares there is nothing left to put here. */}
                {(!rootFolder || onManageShares) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="controlIcon"
                        {...pageInfoTarget("files.more")}
                      >
                        <MoreVertical />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* New folder lives in the title row next to Upload — not
                          duplicated here. */}
                      {onManageShares && (
                        <DropdownMenuItem onClick={onManageShares}>
                          <LinkIcon className="mr-2 h-4 w-4" />
                          {t.documents.manage.manageShares}
                        </DropdownMenuItem>
                      )}
                      {!rootFolder && (
                        <DropdownMenuItem onClick={() => setShowTrash(true)}>
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t.documents.trash.title}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </PageHeaderActions>
            </div>
          </div>
          <div className="flex gap-4">
            {/* Folder tree sidebar - hidden when container is narrow */}
            {showTreeAside && (
              <aside className="hidden w-80 @[1080px]:block" {...pageInfoTarget("files.tree")}>
                <FolderTree
                  folders={folderTreeWithContainers || []}
                  // The rows the root lists, not just the ones with no folder:
                  // unreachable documents are rendered there too, and a badge
                  // that counted only `folder_id IS NULL` would disagree with
                  // them. Counted from the fetched rows, so the cap above can
                  // never make the badge promise a row the list does not have.
                  totalDocumentCount={documentStore.rootDocumentCount + unreachableDocs.length}
                  selectedFolderId={selectedFolderId}
                  activeMove={activeMove}
                  // While a container is open the checked documents are its
                  // members, and a tree drag of an already-checked folder carries
                  // the whole selection — which would move them the same way a
                  // row drag would. The tree drags folders only in that state.
                  dragSelection={{
                    docIds: isViewingContainer ? [] : selectedDocIds,
                    folderIds: selectedFolderIds,
                  }}
                  onInternalDragChange={setActiveMove}
                  onFolderSelect={handleFolderSelect}
                  onSelectRoot={() => handleFolderSelect(null)}
                  onDropDocument={canReorganizeProject ? handleMoveDocument : undefined}
                  onMoveFolder={canReorganizeProject ? handleMoveFolder : undefined}
                  onDropSelection={canReorganizeProject ? handleMoveSelection : undefined}
                  onDropFiles={handleFileDropOnFolder}
                  onRenameFolder={
                    canReorganizeProject
                      ? (folderId, newName) =>
                          updateFolder?.({ folderId, updates: { name: newName } })
                      : undefined
                  }
                  onDeleteFolder={
                    canMoveToTrash ? (folderId) => deleteFolder?.(folderId) : undefined
                  }
                  onExportFolder={handleExportFolder}
                  onOrganizeFolder={canOrganizeFolder ? handleOrganizeFolder : undefined}
                  onShareFolder={handleShareFolder}
                  onCreateSubfolder={(parentId) =>
                    lacksAddFolderPermission
                      ? refuseAdd()
                      : openCreateFolderDialog(parentId ?? null)
                  }
                  isLoading={docsLoading}
                  emptyMessage={t.projects.folder.noFolders}
                  // When the header's project heading is hidden (/documents), the
                  // tree's root item carries root navigation; otherwise the
                  // heading does, so the root item stays hidden.
                  hideRootItem={!hideProjectName}
                  // ...and it's labelled with the project the page nav names,
                  // not a generic "Files".
                  rootName={projectName}
                />
              </aside>
            )}

            {/* Right-click on empty space: New folder / New document in the
                open folder. Not inside an opened container, where neither
                applies. */}
            <FolderViewBackgroundMenu
              disabled={containerStack.length > 0}
              newFolder={newFolderAction}
              newDocument={newDocumentAction}
            >
              <main ref={mainContentRef} className="min-w-0 flex-1">
                {/* Folder path — scoped to the table column, so it sits over the
                  list it navigates and lines up with the project name at the
                  top of the tree beside it. The slot's height is unconditional
                  (min-h-11 = an h-7 segment button in its py-2 row) so entering
                  or leaving a folder swaps the trail in and out without moving
                  the table. */}
                {trailSlot === undefined
                  ? showTrailSlot && (
                      <div className="flex min-h-11 min-w-0 items-center">{trailContent}</div>
                    )
                  : // The host renders the trail in a row of its own (the Brief
                    // page's section header), so the list starts right under it.
                    trailSlot &&
                    showTrailSlot &&
                    createPortal(
                      <div className="flex min-w-0 items-center">{trailContent}</div>,
                      trailSlot
                    )}
                <ProjectDocumentView
                  documents={
                    containerStack.length > 0
                      ? (sortedContainerChildren as any)
                      : (visibleDocuments as any)
                  }
                  isRefreshing={
                    containerStack.length > 0
                      ? false
                      : documentStore.isLoadingProjectDocs ||
                        isSearchInFlight ||
                        isTagFilterFetchInFlight ||
                        isAwaitingFilterOptions
                  }
                  // Container children are never filtered, so their empty list is not a no-match.
                  // A folder with nothing in it is empty, not a no-match: Clear
                  // would change nothing there.
                  hasActiveFilters={
                    containerStack.length === 0 &&
                    hasActiveViewFilters &&
                    (documents.length > 0 || currentSubfolders.length > 0)
                  }
                  onClearFilters={clearViewFilters}
                  buildingId={buildingId}
                  onSigned={refetchDocuments}
                  folders={containerStack.length > 0 ? [] : folderRowsForDisplay}
                  orderedSearchRows={containerStack.length > 0 ? undefined : visibleOrderedRows}
                  onUploadClick={() => setIsUploadModalOpen(true)}
                  onLoadMore={undefined}
                  hasMore={false}
                  isLoadingMore={false}
                  onContainerClick={handleContainerClick}
                  onFolderClick={containerStack.length > 0 ? undefined : handleFolderSelect}
                  onRenameFolder={
                    containerStack.length > 0 || !canReorganizeProject
                      ? undefined
                      : (folderId, newName) =>
                          updateFolder?.({ folderId, updates: { name: newName } })
                  }
                  onDeleteFolder={
                    containerStack.length > 0 || !canMoveToTrash
                      ? undefined
                      : (folderId) => deleteFolder?.(folderId)
                  }
                  onCreateSubfolder={
                    containerStack.length > 0
                      ? undefined
                      : (parentId) =>
                          lacksAddFolderPermission
                            ? refuseAdd()
                            : openCreateFolderDialog(parentId ?? null)
                  }
                  canMoveToTrash={canMoveToTrash}
                  onExportFolder={containerStack.length > 0 ? undefined : handleExportFolder}
                  onOrganizeFolder={
                    containerStack.length > 0 || !canOrganizeFolder
                      ? undefined
                      : handleOrganizeFolder
                  }
                  onShareFolder={containerStack.length > 0 ? undefined : handleShareFolder}
                  onShare={() => setIsShareDialogOpen(true)}
                  onMoveCopy={handleMoveCopy}
                  onAskFranklin={selectedFolderIds.length > 0 ? undefined : handleAskFranklin}
                  hostAction={documentAction}
                  showTags={!hideTags}
                  versionedFolder={viewRootId ? undefined : versionedFolder}
                  onBulkDeleteComplete={async () => {
                    // Trash react-query cache lives outside the MST store, so
                    // bulk-deleting from here doesn't auto-update it. Flush it
                    // alongside the folder tree + document list so navigating
                    // to Trash next shows the just-deleted docs.
                    trashOperations.invalidateDocumentQueries();
                    await Promise.all([refetchFolders(), refetchDocuments()]);
                    // Folder document counts live in a separate Map and aren't
                    // touched by deleteDocument / bulkMoveToTrash, so reload
                    // them explicitly. (WHA-3258)
                    if (projectId) {
                      documentStore.loadFolderDocumentCounts(projectId, buildingId);
                    }
                    setSelectedFolderIds([]);
                  }}
                  onDocumentDeleted={() => {
                    if (projectId) {
                      documentStore.loadFolderDocumentCounts(projectId, buildingId);
                    }
                  }}
                  selectedFolderIds={containerStack.length > 0 ? [] : selectedFolderIds}
                  onFolderSelect={
                    containerStack.length > 0 ? undefined : handleFolderCheckboxToggle
                  }
                  onAddNewVersions={handleAddNewVersions}
                  onRenameDocumentSubmit={handleRenameDocumentSubmit}
                  canRenameDocument={canRenameDocumentInProject}
                  isSelectionPending={pendingCascadeCount > 0}
                  dragDropFolders={folderTreeWithContainers || []}
                  onDropDocument={canMoveFromList ? handleMoveDocument : undefined}
                  onMoveFolder={canMoveFromList ? handleMoveFolder : undefined}
                  onDropSelection={canMoveFromList ? handleMoveSelection : undefined}
                  onDropFiles={handleFileDropOnFolder}
                  onInternalDragChange={canMoveFromList ? setActiveMove : undefined}
                  activeMove={canMoveFromList ? activeMove : undefined}
                />
                {docsLoading && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.projects.folder.loadingDocuments}
                  </div>
                )}
              </main>
            </FolderViewBackgroundMenu>
          </div>
        </div>
      )}

      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        buildingId={buildingId}
        scopedProject={{ id: projectId, name: projectName ?? "" }}
        // Defaults to the open folder; the dialog's destination line lets the
        // user send the batch elsewhere in this project without navigating.
        initialDestination={{ projectId, folderId: selectedFolderId }}
        onUpload={(selection, destination) => void uploadToFolder(selection, destination.folderId)}
      />

      <FolderPickerDialog
        isOpen={isFolderPickerOpen}
        onClose={() => {
          setIsFolderPickerOpen(false);
          setBulkAction(null);
        }}
        onSelectFolder={handleFolderSelected}
        folders={viewRootId ? (rootNode?.children ?? []) : enrichedFolderTree || []}
        rootLabel={rootFolder?.name}
        title={bulkAction === "move" ? "Move to Folder" : "Copy to Folder"}
        isLoading={docsLoading}
      />

      <ProjectSelectCreateModal
        isOpen={isProjectModalOpen}
        onClose={() => {
          setIsProjectModalOpen(false);
          setBulkAction(null);
        }}
        onProjectSelect={handleProjectSelected}
        buildingId={buildingId}
      />

      <CreateFolderDialog
        open={createFolderDialogOpen}
        onOpenChange={(open) => {
          setCreateFolderDialogOpen(open);
          if (!open) setCreateFolderParentId(null);
        }}
        isSubfolder={createFolderParentId !== null}
        onConfirm={(name) => {
          // Re-checked here rather than only on the opening button, same as the
          // New document form: the dialog stays open while the user types, and
          // a refetch in that window can drop the parent out of this project —
          // creating the subfolder outside the project on screen.
          if (lacksAddFolderPermission) {
            refuseAdd();
            return;
          }
          const blockedReason = folderBlockedReason(createFolderParentId);
          if (blockedReason) {
            toast.error(blockedReason);
            return;
          }
          createFolder?.({
            name,
            category: "documents",
            parentId: createFolderParentId ?? undefined,
          });
          setCreateFolderDialogOpen(false);
          setCreateFolderParentId(null);
        }}
      />

      <OrganizeFolderDialog
        open={isOrganizeOpen}
        onOpenChange={setIsOrganizeOpen}
        buildingId={buildingId}
        projectId={projectId}
        folderId={organizeTarget?.id ?? null}
        folderName={organizeTarget?.name ?? ""}
        projectName={projectName ?? ""}
        folderPath={organizeFolderPath}
        onApplied={refreshAfterOrganize}
      />
      {/* The view is always project-scoped, so the flow fetches the project's
          documents itself — the `documents` fallback is for the no-project
          case only and never applies here. */}
      {buildingName != null && (
        <NewDocumentFlow
          open={isNewDocumentModalOpen}
          onOpenChange={setIsNewDocumentModalOpen}
          buildingId={buildingId}
          buildingName={buildingName}
          projectId={projectId}
          documents={[]}
          triggerRef={newDocumentButtonRef}
          // This view is a folder, so a document filed from it belongs in the
          // folder on screen — asking would be asking what is already known.
          saveDestination={{ folderId: selectedFolderId }}
          // Re-checked here rather than only on the button: the form stays open
          // across folder refetches, and filing into a folder that has since
          // left this project is the outcome the gate exists to prevent.
          saveUnavailableReason={folderTargetBlockedReason}
          onDocumentFiled={() => void refetchDocuments()}
        />
      )}

      <ConfirmDialog
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
        onConfirm={handleConfirmPermanentDelete}
        title={t.projects.folder.permanentDeleteTitle}
        description={
          documentToDelete
            ? t.projects.folder.permanentDeleteConfirm.replace(
                "{filename}",
                documentToDelete.filename
              )
            : ""
        }
        confirmText={t.projects.folder.permanentDeleteButton}
        cancelText={t.common.cancel}
        confirmVariant="destructive"
      />

      {/* Upload Queue Progress - fixed position component for bulk uploads */}
      <UploadQueueProgress buildingId={buildingId} />

      {/* Upload Collision Dialog — prompts user when incoming filenames
          clash with existing documents in the target folder. */}
      <UploadCollisionDialog
        open={!!collisionDialog}
        pairs={collisionDialog?.pairs ?? []}
        onResolve={(decisions) => collisionDialog?.resolve(decisions)}
        onCancel={() => collisionDialog?.cancel()}
      />

      {/* Share Dialog - create share link for selected documents/folders */}
      <ShareDialog
        open={isShareDialogOpen}
        onOpenChange={(open) => {
          setIsShareDialogOpen(open);
          // Clear selection when dialog closes after successful share
          if (!open) {
            documentStore.clearSelection();
            setSelectedFolderIds([]);
          }
        }}
        selectedDocumentIds={selectedDocIds}
        selectedFolderIds={selectedFolderIds}
        buildingId={buildingId}
        orgId={currentOrganization?.id || ""}
      />

      {/* Share Folder Dialog - share a single folder from context menu */}
      <ShareDialog
        open={isShareFolderDialogOpen}
        onOpenChange={(open) => {
          setIsShareFolderDialogOpen(open);
          if (!open) {
            setShareFolderId(null);
          }
        }}
        selectedDocumentIds={[]}
        selectedFolderIds={shareFolderId ? [shareFolderId] : []}
        buildingId={buildingId}
        orgId={currentOrganization?.id || ""}
      />

      {/* Project Destination Modal - move/copy documents */}
      <ProjectDestinationModal
        isOpen={isProjectDestinationOpen}
        onClose={() => setIsProjectDestinationOpen(false)}
        onConfirm={handleProjectDestinationConfirm}
        projects={projects}
        currentProjectId={projectId}
        buildingId={buildingId}
        itemCount={selectedDocIds.length + selectedFolderIds.length}
        isLoading={isProjectOperationLoading}
      />
    </section>
  );
});
